import { Dialog } from "./catalyst/dialog";
import { Hex } from "viem";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Chain,
  createSession,
  createSessionFromWidget,
  Currency,
  estimatePaymentAmount,
  getEIP155ChainId,
  GlideOverCapacityError,
  InsufficientPaymentAmountError,
  listPaymentOptions,
  listSupportedCurrencies,
  makeRequest,
  PaymentMethod,
  PaymentOption,
  Session,
} from "@paywithglide/glide-js";

import { CircleX, LoaderCircle, X } from "lucide-react";
import TransactionCheckoutPaymentOptionsList from "./steps/TransactionCheckoutPaymentOptionsList";
import TransactionCheckoutSuccess from "./steps/TransactionCheckoutSuccess";
import PayWithCoinbaseOnramp from "./steps/PayWithCoinbaseOnramp";
import DepositAmount from "./steps/DepositAmount";
import GlideLogo from "./logos/GlideLogo";
import ConnectWallet from "./steps/ConnectWallet";
import { useGlideDepositConfig } from "./lib/useDepositConfig";
import TransactionCheckoutHeader from "./components/TransactionCheckoutHeader";
import { AnimateChangeInHeight } from "./components/AnimateChangeInHeight";
import PayWithMoonPay from "./steps/PayWithMoonPay";
import PayWithOnramp from "./steps/PayWithOnramp";
import PayWithOnramper from "./steps/PayWithOnramper";
import TransferChooseChain from "./steps/TransferChooseChain";
import Transfer from "./steps/Transfer";
import MultichainTransfer from "./steps/MultichainTransfer";

import { getConnectedWallet } from "./lib/getConnectedWallet";
import RecipientWalletAddress from "./steps/RecipientWalletAddress";
import ModeSelector from "./steps/ModeSelector";
import PayWithInterac from "./steps/PayWithInterac";
import React from "react";
import { ChooseCurrency } from "./steps/ChooseCurrency";
import SupportedChainsList from "./steps/SupportedChainsList";
import { widgetAnalytics } from "./lib/analytics";
import {
  base,
  bitcoin,
  bitcoinTestnet,
  ethereum,
  lightning,
  polygonAmoy,
  sx,
} from "@paywithglide/glide-js/chains";
import { btc, eth, sol, usdc, usdt } from "@paywithglide/glide-js/currencies";
import PayWithCoinbaseApp from "./steps/PayWithCoinbaseApp";
import { createGlideTx } from "./lib/createGlideTx";
import { ReviewSession } from "./steps/ReviewSession";
import { FundingSource } from "./steps/FundingSource";
import { OnrampProviderSelector } from "./steps/OnrampProviderSelector";
import { CryptoFundingSource } from "./steps/CryptoFundingSource";
import { WithdrawDestination } from "./steps/WithdrawDestination";
import { WithdrawPaymentMethod } from "./steps/WithdrawPaymentMethod";
import WithdrawWithInterac from "./steps/WithdrawWithInterac";
import { filterPaymentChainIds } from "./lib/filterPaymentChainIds";
import { glideLocalStorage } from "./lib/glideLocalStorage";
import { useConnectedWallet } from "./lib/useConnectedWallet";
import { useGlideWallet } from "./components/wallet/GlideWalletProvider";
import { saveLastDepositAmount } from "./lib/userPreferencesStorage";
import { DebugStep } from "./steps/DebugStep";
import { GasRefillExplainer } from "./steps/GasRefillExplainer";
import InsufficientPaymentAmount from "./steps/InsufficientPaymentAmount";
import ConfirmRefund from "./steps/ConfirmRefund";
import OverpaymentRefund from "./steps/OverpaymentRefund";
import { logger } from "./lib/logger";
import { supportedChains } from "./lib/glide";
import { stableDepositAddressKeyPayload } from "./lib/stableDepositAddressKey";
import { processCurrencies } from "./lib/processCurrencies";
import { GlideRoute, listGlideRoutes } from "./lib/listGlideRoutes";
import {
  chainTypeFromCaip,
  loadTokenSelection,
  SavedTokenSelection,
} from "./lib/tokenSelectionStorage";
import {
  findFirstSupportedChainSelection,
  findSupportedChainsForCurrency,
  getAllowedMultichainChains,
  matchesMultichainType,
  MultichainChainType,
} from "./lib/multichainEligibility";

const getAllowedPaymentCurrencies = async (
  glideConfig: { projectId: string; chains: Chain[] },
  allowedCurrencyIds: string[],
  paymentChainIds: string[],
): Promise<Currency[]> => {
  // If paymentChainIds is empty, use all chains from glideConfig
  const chainsToCheck =
    paymentChainIds.length > 0
      ? paymentChainIds
      : glideConfig.chains.map((chain) => chain.id);

  try {
    // Get currencies for all chains in one call
    const allCurrencies = await listSupportedCurrencies(glideConfig, {
      chainIds: chainsToCheck as `eip155:${number}`[],
      all: true,
    });

    // Filter to only allowed currency IDs
    const filteredCurrencies = allCurrencies.filter((currency) =>
      allowedCurrencyIds.includes(currency.id),
    );

    // Sort by order in allowedCurrencyIds to preserve the configured order
    const allowedIdsLowerCase = allowedCurrencyIds.map((id) =>
      id.toLowerCase(),
    );
    filteredCurrencies.sort((a, b) => {
      const aIndex = allowedIdsLowerCase.indexOf(a.id.toLowerCase());
      const bIndex = allowedIdsLowerCase.indexOf(b.id.toLowerCase());
      return aIndex - bIndex;
    });

    return filteredCurrencies;
  } catch {
    return [];
  }
};

const chainIdFromPaymentCurrency = (paymentCurrency: string): string =>
  paymentCurrency.split("/")[0];

// Resolves a CAIP-19 payment currency into the shape the saved-selection
// restore path expects, so an app-provided default can reuse it.
const tokenSelectionFromPaymentCurrency = (
  currencies: Currency[],
  chains: Chain[],
  paymentCurrency: string,
):
  | Pick<SavedTokenSelection, "currencyId" | "chainType" | "chainId">
  | undefined => {
  const chainId = chainIdFromPaymentCurrency(paymentCurrency);
  const chainType = chainTypeFromCaip(chainId);
  const chain = chains.find((candidate) => candidate.id === chainId);

  if (!chainType || !chain) {
    return undefined;
  }

  const currency = currencies.find((candidate) => {
    try {
      return (
        candidate.on(chain).toLowerCase() === paymentCurrency.toLowerCase()
      );
    } catch {
      return false;
    }
  });

  return currency ? { currencyId: currency.id, chainType, chainId } : undefined;
};

const expandAllowedCurrencyIdsBySymbol = (
  currencies: Currency[],
  allowedCurrencyIds?: string[],
): string[] | undefined => {
  if (!allowedCurrencyIds?.length) {
    return allowedCurrencyIds;
  }

  const allowedIds = new Set(allowedCurrencyIds.map((id) => id.toLowerCase()));
  const matchedSymbols = new Set(
    currencies
      .filter((currency) => allowedIds.has(currency.id.toLowerCase()))
      .map((currency) => currency.symbol.toLowerCase()),
  );

  if (!matchedSymbols.size) {
    return allowedCurrencyIds;
  }

  return currencies
    .filter((currency) => matchedSymbols.has(currency.symbol.toLowerCase()))
    .map((currency) => currency.id);
};

const getRefundModeTargetStep = (
  session: Session,
  refundToken?: string,
): "insufficient_payment_amount" | "excess_payment_refund" | "error" => {
  const sessionRefundToken = (session as Record<string, unknown>).refundToken;
  const effectiveRefundToken =
    typeof sessionRefundToken === "string" && sessionRefundToken
      ? sessionRefundToken
      : refundToken;

  if (!effectiveRefundToken) {
    return "error";
  }

  const requiredAmount = Number(session.paymentAmountUSD || 0);
  const paidAmount = Number(session.actualPaymentAmountUSD || 0);

  if (
    session.paymentStatus === "pending_refund" ||
    session.paymentStatus === "refunded"
  ) {
    return "insufficient_payment_amount";
  }

  if (paidAmount > requiredAmount) {
    return "excess_payment_refund";
  }

  return "error";
};

export type Props = {
  open: boolean;
  onClose: () => void;
};

export default function DepositWithGlideModal({ open, onClose }: Props) {
  const { glideConfig, refundSession, ...depositConfig } =
    useGlideDepositConfig();

  const {
    isConnected,
    canDisconnect,
    address: connectedAddress,
  } = useConnectedWallet();
  const { awaitDepositOwnerAddress, isReady: isWalletReady } = useGlideWallet();

  // Detect if onramp is the only payment method
  const isOnrampOnly =
    depositConfig.paymentMethods.length === 1 &&
    depositConfig.paymentMethods[0] === "onramp";

  // Track wallet address changes for analytics
  useEffect(() => {
    widgetAnalytics.setWalletAddress(connectedAddress);
  }, [connectedAddress]);

  // Track modal opened
  useEffect(() => {
    if (open) {
      widgetAnalytics.modalOpened();
    }
  }, [open]);

  // In pay/call mode with a fixed USD amount, check whether Bitcoin is viable
  // (below 0.0001 BTC the provider's flat deposit fee eats too much of it)
  const MIN_BTC_AMOUNT = 0.0001;
  useEffect(() => {
    if (
      !(depositConfig.mode === "pay" || depositConfig.mode === "call") ||
      !depositConfig.hasFixedAmount
    ) {
      setIsBitcoinBelowMinimum(false);
      return;
    }

    // amountUSD takes precedence; fall back to amount (treated as USD in pay/call mode)
    const rawAmount = depositConfig.amountUSD || depositConfig.amount;
    if (!rawAmount) {
      setIsBitcoinBelowMinimum(false);
      return;
    }

    const amountUSD = parseFloat(rawAmount);
    if (isNaN(amountUSD)) return;

    makeRequest<{ price: string }>(glideConfig, {
      url: "/marketdata/price",
      queryParams: { currency: btc.on(bitcoin) },
    })
      .then(({ price }) => {
        const btcPrice = parseFloat(price);
        if (btcPrice > 0) {
          setIsBitcoinBelowMinimum(amountUSD / btcPrice < MIN_BTC_AMOUNT);
        }
      })
      .catch(() => {
        // If price fetch fails, don't filter — let the backend handle it
        setIsBitcoinBelowMinimum(false);
      });
  }, [
    glideConfig,
    depositConfig.mode,
    depositConfig.hasFixedAmount,
    depositConfig.amountUSD,
    depositConfig.amount,
  ]);

  const [step, setStep] = useState<
    | "mode_selector"
    | "buy_chain"
    | "buy_token_selector"
    | "withdraw_payment_method"
    | "withdraw_interac_login"
    | "withdraw_dest"
    | "withdraw_dest_chain"
    | "withdraw_dest_currency"
    | "recipient"
    | "deposit_amount"
    | "estimate_transaction_amount"
    | "creating_session"
    | "funding_source"
    | "onramp_provider_selector"
    | "crypto_funding_source"
    | "connect_wallet"
    | "payment_options"
    | "wait_for_session_challenge"
    | "pay_with_coinbase_onramp"
    | "pay_with_coinbase_app"
    | "pay_with_moonpay"
    | "pay_with_onramp"
    | "pay_with_interac"
    | "pay_with_onramper"
    | "transfer_choose_chain"
    | "transfer"
    | "initializing_transfer"
    | "multichain_transfer"
    | "multichain_supported_tokens_chain"
    | "multichain_supported_tokens_currency"
    | "multichain_supported_chains_for_currency"
    | "multichain_view_supported_chains"
    | "review"
    | "gas_refill_explainer"
    | "success"
    | "debug"
    | "error"
    | "insufficient_payment_amount"
    | "confirm_refund"
    | "excess_payment_refund"
  >(() => {
    const isOnrampOnly =
      depositConfig.paymentMethods.length === 1 &&
      depositConfig.paymentMethods[0] === "onramp";

    // Early return for onramp-only in deposit, pay, or call modes
    if (
      isOnrampOnly &&
      (depositConfig.mode === "deposit" ||
        depositConfig.mode === "pay" ||
        depositConfig.mode === "call")
    ) {
      return "creating_session";
    }

    if (!depositConfig.mode) {
      return "mode_selector";
    } else if (depositConfig.mode === "refund") {
      return "creating_session"; // Show loading while refund session loads
    } else if (depositConfig.mode === "buy") {
      return "buy_chain";
    } else if (depositConfig.mode === "deposit") {
      if (
        depositConfig.paymentMethods.length === 1 &&
        depositConfig.paymentMethods[0] === "transfer"
      ) {
        return "initializing_transfer";
      }
      return "funding_source";
    } else if (depositConfig.mode === "withdraw") {
      return "withdraw_payment_method";
    } else if (
      depositConfig.mode === "pay" &&
      depositConfig.paymentMethods.length === 1 &&
      depositConfig.paymentMethods[0] === "transfer"
    ) {
      return "initializing_transfer";
    } else if (depositConfig.mode === "pay" || depositConfig.mode === "call") {
      return "estimate_transaction_amount";
    } else {
      return "error";
    }
  });
  const [stepStack, setStepStack] = useState<(typeof step)[]>([step]);
  const [glideSession, setGlideSession] = useState<Session>();
  const [routes, setRoutes] = useState<GlideRoute[]>();
  const [bitcoinLightningSession, setBitcoinLightningSession] =
    useState<Session>();
  const [paymentMethod, setPaymentMethod] = useState<
    | "wallet"
    | "external_wallet"
    | "transfer"
    | "coinbase"
    | "coinbase_app"
    | "fiat"
    | "interac"
    | "debit_card_alias"
    | "onramp"
    | "onramper"
  >();

  const [txAmountUSD, setTxAmountUSD] = useState<number>();

  const [withdrawDestinationChain, setWithdrawDestinationChain] =
    useState<Chain>();
  const [withdrawCurrencies, setWithdrawCurrencies] = useState<Currency[]>();
  const [selectedWithdrawCurrency, setSelectedWithdrawCurrency] =
    useState<Currency>();
  const [depositAmount, setDepositAmount] = useState(
    depositConfig.amount && Number(depositConfig.amount) > 0
      ? depositConfig.amount
      : "",
  );
  const [depositAmountDenomination, setDepositAmountDenomination] = useState<
    "token" | "usd"
  >("usd");

  // Initialize denomination state based on configuration
  React.useEffect(() => {
    const initialDenomination =
      depositConfig.tokenAmountDefault &&
      paymentMethod === "wallet" &&
      !depositConfig.hideSettlementCopy
        ? "token"
        : depositConfig.amount &&
            Number(depositConfig.amount) > 0 &&
            !depositConfig.hideSettlementCopy
          ? "token"
          : (depositConfig.currency!.id === usdc.id ||
                depositConfig.mode === "withdraw") &&
              !depositConfig.hideSettlementCopy
            ? "token"
            : (depositConfig.mode === "deposit" ||
                  depositConfig.mode === "buy") &&
                !depositConfig.hideSettlementCopy
              ? depositConfig.defaultDepositTokenDenomination || "usd"
              : "usd";

    setDepositAmountDenomination(initialDenomination);
  }, [
    depositConfig.tokenAmountDefault,
    paymentMethod,
    depositConfig.amount,
    depositConfig.currency,
    depositConfig.mode,
    depositConfig.defaultDepositTokenDenomination,
    depositConfig.hideSettlementCopy,
  ]);

  const [isOverCapacity, setIsOverCapacity] = useState(false);
  const [isInsufficientPaymentAmount, setIsInsufficientPaymentAmount] =
    useState(false);
  const [paymentOptions, setPaymentOptions] = useState<
    PaymentOption[] | undefined
  >();
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<PaymentOption>();
  const [pendingAutoSelectPaymentOption, setPendingAutoSelectPaymentOption] =
    useState<PaymentOption | null>(null);
  const [isUpdatingSession, setIsUpdatingSession] = useState(false);
  const [isCreatingWalletSession, setIsCreatingWalletSession] = useState(false);
  const [isLoadingPaymentOptions, setIsLoadingPaymentOptions] = useState(false);
  const [isCreatingCoinbaseAppSession, setIsCreatingCoinbaseAppSession] =
    useState(false);
  const [isCreatingCoinbaseOnrampSession, setIsCreatingCoinbaseOnrampSession] =
    useState(false);
  const [isCreatingMoonpayOnrampSession, setIsCreatingMoonpayOnrampSession] =
    useState(false);
  const [isCreatingOnrampSession, setIsCreatingOnrampSession] = useState(false);
  const [isCreatingInteracSession, setIsCreatingInteracSession] =
    useState(false);
  const [isCreatingOnramperSession, setIsCreatingOnramperSession] =
    useState(false);
  const onramperPopupRef = useRef<Window | null>(null);
  const [isLoadingTransferCurrencies, setIsLoadingTransferCurrencies] =
    useState(false);
  const [walletHasNoPaymentOptions, setWalletHasNoPaymentOptions] =
    useState(false);
  const [fundingSourceError, setFundingSourceError] = useState<string | null>(
    null,
  );
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    string[] | null
  >(null);
  const [isLoadingCapabilities, setIsLoadingCapabilities] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>();
  const [transferCurrencies, setTransferCurrencies] = useState<Currency[]>();
  const [chainsToDisplay, setChainsToDisplay] = useState<Chain[]>([]);
  const hasInitializedTransferOnlyRef = useRef(false);

  const [coinbaseAccessToken, setCoinbaseAccessToken] = useState("");
  const [selectedMultichainType, setSelectedMultichainType] = useState<
    "evm" | "solana" | "bitcoin" | "tron"
  >();
  const [selectedMultichainCurrency, setSelectedMultichainCurrency] =
    useState<Currency>();
  const [selectedMultichainCurrencies, setSelectedMultichainCurrencies] =
    useState<Currency[]>();

  // Multichain session states
  const [multichainEvmSession, setMultichainEvmSession] = useState<Session>();
  const [multichainSolanaSession, setMultichainSolanaSession] =
    useState<Session>();
  const [multichainBitcoinSession, setMultichainBitcoinSession] =
    useState<Session>();
  const [isBitcoinBelowMinimum, setIsBitcoinBelowMinimum] = useState(false);
  const [
    multichainBitcoinLightningSession,
    setMultichainBitcoinLightningSession,
  ] = useState<Session>();
  const [multichainTronSession, setMultichainTronSession] = useState<Session>();

  // Store session that failed with insufficient payment amount
  const [failedSession, setFailedSession] = useState<Session | undefined>(
    depositConfig.mode === "refund" ? refundSession : undefined,
  );
  const [refundToken, setRefundToken] = useState<string | undefined>(
    depositConfig.refundToken,
  );

  // Track Option/Alt key state for debug access
  const [isOptionKeyPressed, setIsOptionKeyPressed] = useState(false);

  // Track ongoing capabilities fetch to avoid duplicate requests
  const capabilitiesFetchPromise = useRef<Promise<void> | null>(null);

  // Fetch available payment methods on demand (when user clicks onramp)
  const fetchAvailablePaymentMethods = useCallback(async () => {
    // If already loaded, skip
    if (availablePaymentMethods !== null) return;

    // If currently loading, wait for it to complete
    if (capabilitiesFetchPromise.current) {
      await capabilitiesFetchPromise.current;
      return;
    }

    // Start new fetch
    const fetchPromise = (async () => {
      setIsLoadingCapabilities(true);
      try {
        const response = await makeRequest<{
          availablePaymentMethods: string[];
        }>(glideConfig, {
          url: `/project-capabilities?projectId=${glideConfig.projectId}`,
          method: "GET",
        });
        setAvailablePaymentMethods(response.availablePaymentMethods);
      } catch (e) {
        console.error("Failed to fetch available payment methods:", e);
        // On error, allow all payment methods (graceful degradation)
        setAvailablePaymentMethods(null);
      } finally {
        setIsLoadingCapabilities(false);
        capabilitiesFetchPromise.current = null;
      }
    })();

    capabilitiesFetchPromise.current = fetchPromise;
    await fetchPromise;
  }, [glideConfig, availablePaymentMethods]);

  const rememberRefundToken = useCallback((session?: Session) => {
    const sessionRefundToken = (session as Record<string, unknown> | undefined)
      ?.refundToken;
    if (typeof sessionRefundToken === "string" && sessionRefundToken) {
      setRefundToken(sessionRefundToken);
    }
  }, []);

  const resetState = useCallback(() => {
    setGlideSession(undefined);
    setBitcoinLightningSession(undefined);
    setPaymentMethod(undefined);
    setWithdrawDestinationChain(undefined);
    setWithdrawCurrencies([]);
    setSelectedWithdrawCurrency(undefined);
    setDepositAmount(depositConfig.amount || "");
    setPaymentOptions(undefined);
    setSelectedPaymentOption(undefined);
    setCurrencyOptions([]);
    setIsOverCapacity(false);
    setIsInsufficientPaymentAmount(false);
    setFundingSourceError(null);
    // Reset multichain sessions
    setMultichainEvmSession(undefined);
    setMultichainSolanaSession(undefined);
    setMultichainBitcoinSession(undefined);
    setMultichainBitcoinLightningSession(undefined);
    setMultichainTronSession(undefined);
    setSelectedMultichainCurrency(undefined);
    setFailedSession(undefined);
    setRefundToken(depositConfig.refundToken);
    setWalletHasNoPaymentOptions(false);
    hasInitializedTransferOnlyRef.current = false;
  }, [depositConfig.amount, depositConfig.refundToken]);

  const navigateBack = () => {
    if (stepStack.length < 2) {
      navigateStartOver();
      return;
    }

    const currentStep = step;
    const prevStep = stepStack[stepStack.length - 2];

    if (stepStack.length === 2) {
      navigateStartOver();
      return;
    }

    // Track step exit
    widgetAnalytics.stepExited(currentStep, "back");

    // Check if session is expired or if going to a previous step
    // Reset session if expired (check expiresAt) or navigating backwards to steps that shouldn't have a session
    if (glideSession) {
      const now = Date.now();
      // Check for expiresAt property on session (may not be in type definition)
      const sessionRecord = glideSession as Session & Record<string, unknown>;
      const expiresAt = sessionRecord.expiresAt as
        | number
        | Date
        | string
        | undefined;

      // Check if session is expired
      let isExpired = false;
      if (expiresAt !== undefined && expiresAt !== null) {
        if (typeof expiresAt === "number") {
          isExpired = now >= expiresAt;
        } else if (typeof expiresAt === "string") {
          // Handle ISO string format
          const expiryDate = new Date(expiresAt);
          isExpired =
            !isNaN(expiryDate.getTime()) && now >= expiryDate.getTime();
        } else {
          // Handle Date object - use type assertion since we've narrowed the type
          const dateValue = expiresAt as Date;
          isExpired = now >= dateValue.getTime();
        }
      }

      // Steps that should preserve session when navigating back
      const stepsThatPreserveSession = [
        "review",
        "transfer",
        "multichain_transfer",
        "pay_with_coinbase_onramp",
        "pay_with_moonpay",
        "pay_with_interac",
        "pay_with_coinbase_app",
        "pay_with_onramper",
      ];

      // Reset session if expired or if going back to a step that shouldn't have a session
      if (isExpired || !stepsThatPreserveSession.includes(prevStep)) {
        setGlideSession(undefined);
        setRoutes(undefined);
      }
    }

    setStepStack(stepStack.slice(0, -1));
    setStep(prevStep);

    // Track new step viewed
    widgetAnalytics.stepViewed(prevStep);

    if (prevStep === "funding_source") {
      resetState();
    }

    // Reset loading states when navigating back
    setIsUpdatingSession(false);
    setIsCreatingWalletSession(false);
    setIsCreatingCoinbaseAppSession(false);
    setIsCreatingCoinbaseOnrampSession(false);
    setIsCreatingMoonpayOnrampSession(false);
    setIsCreatingInteracSession(false);
    setIsCreatingOnramperSession(false);
  };

  const navigateStartOver = useCallback(() => {
    const newStep = stepStack[0] ?? step;
    setStepStack([newStep]);
    setStep(newStep);
    resetState();
  }, [resetState, step, stepStack]);

  const navigateToStep = useCallback(
    (newStep: typeof step, replace?: boolean) => {
      if (newStep === step) return;

      // Track step exit
      widgetAnalytics.stepExited(step, "forward");

      if (replace) {
        setStepStack((prevStack) => [...prevStack.slice(0, -1), newStep]);
      } else {
        setStepStack((prevStack) => [...prevStack, newStep]);
      }

      setStep(newStep);

      // Track new step viewed
      widgetAnalytics.stepViewed(newStep);
    },
    [step],
  );

  useEffect(() => {
    rememberRefundToken(refundSession);
    rememberRefundToken(glideSession);
    rememberRefundToken(bitcoinLightningSession);
    rememberRefundToken(failedSession);
    rememberRefundToken(multichainEvmSession);
    rememberRefundToken(multichainSolanaSession);
    rememberRefundToken(multichainBitcoinSession);
    rememberRefundToken(multichainBitcoinLightningSession);
    rememberRefundToken(multichainTronSession);
  }, [
    rememberRefundToken,
    refundSession,
    glideSession,
    bitcoinLightningSession,
    failedSession,
    multichainEvmSession,
    multichainSolanaSession,
    multichainBitcoinSession,
    multichainBitcoinLightningSession,
    multichainTronSession,
  ]);

  const initEstimateTransactionAmount = useRef(false);
  useEffect(() => {
    const estimateTxAmount = async () => {
      try {
        switch (depositConfig.mode) {
          case "pay": {
            // If amountUSD is provided, use it directly
            if (
              depositConfig.amountUSD &&
              Number(depositConfig.amountUSD) > 0
            ) {
              setTxAmountUSD(Number(depositConfig.amountUSD));
              if (isOnrampOnly) {
                navigateToStep("creating_session", true);
              } else {
                navigateToStep("funding_source", true);
              }
              break;
            }

            // If no amount is provided, go to funding_source step for user to select funding method
            if (!depositAmount || Number(depositAmount) <= 0) {
              // Skip to onramp if it's the only option
              if (isOnrampOnly) {
                navigateToStep("creating_session", true);
              } else {
                navigateToStep("funding_source", true);
              }
              break;
            }

            const { price } = await makeRequest<{ price: string }>(
              glideConfig,
              {
                url: "/marketdata/price",
                queryParams: {
                  currency: depositConfig.currency!.on(depositConfig.chain!),
                },
              },
            );

            setTxAmountUSD(Number(price) * Number(depositAmount));

            // Skip to onramp if it's the only option
            if (isOnrampOnly) {
              navigateToStep("creating_session", true);
            } else {
              navigateToStep("funding_source", true);
            }

            break;
          }
          case "call": {
            const tx = await createGlideTx({ glideConfig, ...depositConfig });

            const txChain = supportedChains.find(
              (c) => getEIP155ChainId(c.id) === depositConfig.evmTx?.chainId,
            );

            if (!txChain) throw new Error("Unsupported chain");

            // @ts-expect-error: createTx return type is not properly typed
            const pa = await estimatePaymentAmount(glideConfig, {
              paymentCurrency: txChain ? usdc.on(polygonAmoy) : usdc.on(base),
              ...tx,
            });

            depositConfig.setChain(txChain);
            setTxAmountUSD(Number(pa.paymentAmountUSD));

            // Skip to onramp if it's the only option
            if (isOnrampOnly) {
              navigateToStep("creating_session", true);
            } else {
              navigateToStep("funding_source", true);
            }

            break;
          }
          default:
            throw new Error(`Unsupported mode: ${depositConfig.mode}`);
        }
      } catch (error) {
        logger.error(`Error estimating transaction amount: ${error}`);
        // Navigate to funding_source even on error so user isn't stuck
        if (isOnrampOnly) {
          navigateToStep("creating_session", true);
        } else {
          navigateToStep("funding_source", true);
        }
      }
    };

    if (initEstimateTransactionAmount.current) return;
    initEstimateTransactionAmount.current = true;

    if (step !== "estimate_transaction_amount") return;

    estimateTxAmount();
  }, [
    step,
    depositAmount,
    glideConfig,
    depositConfig.chain,
    depositConfig.currency,
    navigateToStep,
    depositConfig,
    isOnrampOnly,
  ]);

  const createGlideSession = useCallback(
    async (
      d?: string,
      c?: Chain,
      r?: Hex,
      routePriority?: "speed" | "cost",
    ) => {
      // Don't create session via this function if onramp-only mode
      // The useEffect will handle it instead
      if (isOnrampOnly) {
        return;
      }

      // amountUSD path: backend computes the token amount
      if (depositConfig.amountUSD && Number(depositConfig.amountUSD) > 0) {
        setPaymentMethod("wallet");
        setIsUpdatingSession(true);
        setIsCreatingWalletSession(true);
        setIsOverCapacity(false);
        setIsInsufficientPaymentAmount(false);

        const chain = c || depositConfig.chain;
        const recipient = r || depositConfig.recipient;
        if (!chain || !recipient) return;

        try {
          widgetAnalytics.sessionCreationStarted();

          const { address, availableChainIds } = await getConnectedWallet();

          let paymentCurrency =
            selectedPaymentOption?.paymentCurrency ||
            depositConfig.currency!.on(depositConfig.chain!);

          // If no selected payment option, list payment options first
          if (
            !selectedPaymentOption &&
            depositConfig.allowedPaymentCurrencies?.length
          ) {
            const depositOnlyChains = ["eip155:41454", "eip155:8453001"];
            const walletSupportedChains = availableChainIds?.filter(
              (chainId) => !depositOnlyChains.includes(chainId),
            );

            const paymentChainIds = filterPaymentChainIds(
              depositConfig.chainIds,
              walletSupportedChains,
              depositConfig.excludeChainIds,
            );

            const paymentCurrencies = await getAllowedPaymentCurrencies(
              glideConfig,
              depositConfig.allowedPaymentCurrencies,
              paymentChainIds || [],
            );

            const paymentOptions = await listPaymentOptions(glideConfig, {
              paymentChainIds,
              account: address,
              includeInsufficientBalanceOptions: true,
              commissionRates: depositConfig.commissionRates,
              excludeChainIds: depositConfig.excludeChainIds,
              excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
              paymentCurrencies,
              transfer: {
                token: depositConfig.currency!.contractAddressOn!(chain),
                recipient: recipient,
                amount: "1",
              },
              chainId: chain.id,
            });

            if (!paymentOptions.length) {
              setPaymentOptions([]);
              setGlideSession(undefined);
              setIsUpdatingSession(false);
              setIsCreatingWalletSession(false);
              navigateStartOver();
              setWalletHasNoPaymentOptions(true);
              return;
            }

            paymentCurrency = paymentOptions[0].paymentCurrency;
            setPaymentOptions(paymentOptions);
            if (depositConfig.allowedPaymentCurrencies?.length === 1) {
              setIsCreatingWalletSession(false);
              setPendingAutoSelectPaymentOption(paymentOptions[0]);
              return;
            }
            navigateToStep("payment_options");
            setIsCreatingWalletSession(false);
            return;
          }

          // Build Transfer struct (backend fills the real amount from paymentAmountUSD)
          const depositOwnerAddress = await awaitDepositOwnerAddress(
            chainIdFromPaymentCurrency(paymentCurrency),
          );
          const session = await createSession(glideConfig, {
            account: address,
            paymentCurrency,
            paymentMethod: "wallet",
            paymentAmountUSD: depositConfig.amountUSD,
            transfer: {
              token: depositConfig.currency!.contractAddressOn!(chain),
              recipient: recipient,
              amount: "0",
            },
            chainId: chain.id,
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            depositOwnerAddress,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            routePriority,
          });

          const routes = await listGlideRoutes(glideConfig, {
            paymentCurrency,
            spendCurrency: depositConfig.currency!.on(depositConfig.chain!),
          });

          widgetAnalytics.sessionCreated(
            session.sessionId,
            "wallet",
            txAmountUSD,
          );

          // Set depositAmount from session response for display in review step
          setDepositAmount(session.paymentAmount.toString());
          setGlideSession(session);
          setRoutes(routes);
          setIsUpdatingSession(false);
          setIsCreatingWalletSession(false);

          // Save deposit amount preference when proceeding to review
          await saveLastDepositAmount(
            session.paymentAmount.toString(),
            depositAmountDenomination,
          );

          navigateToStep("review");
          return session;
        } catch (e) {
          if (e instanceof GlideOverCapacityError) {
            setIsOverCapacity(true);
            widgetAnalytics.errorOccurred(
              "over_capacity",
              "Service at capacity",
            );
            navigateToStep("deposit_amount", true);
            setIsUpdatingSession(false);
            setIsCreatingWalletSession(false);
            return;
          }
          if (e instanceof InsufficientPaymentAmountError) {
            setIsInsufficientPaymentAmount(true);
            widgetAnalytics.errorOccurred(
              "insufficient_payment",
              "Payment amount too low",
            );
            navigateToStep("deposit_amount", true);
            setIsUpdatingSession(false);
            setIsCreatingWalletSession(false);
            return;
          }

          widgetAnalytics.sessionCreationFailed(String(e));
          throw e;
        }
      }

      const dep = d || depositAmount;
      const chain = c || withdrawDestinationChain || depositConfig.chain;
      const recipient = r || depositConfig.recipient;

      if (
        (depositConfig.mode === "call" && !chain) ||
        (depositConfig.mode !== "call" && (!dep || !chain || !recipient))
      ) {
        return;
      }

      try {
        setPaymentMethod("wallet");
        setIsUpdatingSession(true);
        setIsCreatingWalletSession(true);
        setIsOverCapacity(false);
        setIsInsufficientPaymentAmount(false);

        // Track session creation started
        widgetAnalytics.sessionCreationStarted();

        // For pay/call modes, we need to get the currency object for the destination chain
        let currencyForTx = undefined;
        if (
          (depositConfig.mode === "pay" ||
            depositConfig.mode === "call" ||
            (depositConfig.mode === "deposit" &&
              depositConfig.hideSettlementCopy)) &&
          selectedPaymentOption &&
          chain
        ) {
          // Get currency object for the destination chain
          try {
            const currencies = await listSupportedCurrencies(glideConfig, {
              chainId: chain.id,
              all: true,
            });
            // Find the destination currency (depositConfig.currency) on the destination chain
            currencyForTx = depositConfig.currency
              ? currencies.find(
                  (c) => c.symbol === depositConfig.currency!.symbol,
                )
              : currencies[0];
          } catch (error) {
            logger.error(`Error fetching currency for pay mode: ${error}`);
          }
        }

        const tx = await createGlideTx(
          { glideConfig, ...depositConfig },
          {
            amount: dep,
            chain,
            currency:
              depositConfig.mode === "withdraw"
                ? selectedWithdrawCurrency
                : depositConfig.mode === "pay" ||
                    depositConfig.mode === "call" ||
                    (depositConfig.mode === "deposit" &&
                      depositConfig.hideSettlementCopy)
                  ? currencyForTx
                  : undefined,
            recipient,
          },
        );
        const { address, availableChainIds } = await getConnectedWallet();

        let paymentCurrency =
          selectedPaymentOption?.paymentCurrency ||
          depositConfig.currency!.on(depositConfig.chain!);

        // For pay/call mode with allowedPaymentCurrencies, always get payment options to ensure filtering
        if (
          !selectedPaymentOption &&
          (depositConfig.mode === "deposit" ||
            depositConfig.mode === "buy" ||
            depositConfig.mode === "pay" ||
            depositConfig.mode === "call")
        ) {
          // Filter out deposit-only chains that don't support wallet payments
          const depositOnlyChains = ["eip155:41454", "eip155:8453001"]; // HyperEVM, Hypercore
          const walletSupportedChains = availableChainIds?.filter(
            (chainId) => !depositOnlyChains.includes(chainId),
          );

          const paymentChainIds = filterPaymentChainIds(
            depositConfig.chainIds,
            walletSupportedChains,
            depositConfig.excludeChainIds,
          );

          let paymentCurrencies: Currency[] | undefined;
          if (depositConfig.allowedPaymentCurrencies?.length) {
            paymentCurrencies = await getAllowedPaymentCurrencies(
              glideConfig,
              depositConfig.allowedPaymentCurrencies,
              paymentChainIds || [],
            );
          }

          // @ts-expect-error: createTx return type is not properly typed
          const paymentOptions = await listPaymentOptions(glideConfig, {
            paymentChainIds,
            account: address,
            includeInsufficientBalanceOptions: true,
            commissionRates: depositConfig.commissionRates,
            excludeChainIds: depositConfig.excludeChainIds,
            excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
            ...(paymentCurrencies && { paymentCurrencies }),
            ...tx,
          });

          if (!paymentOptions.length) {
            setPaymentOptions([]);
            setGlideSession(undefined);
            setIsUpdatingSession(false);
            setIsCreatingWalletSession(false);
            navigateStartOver();
            setWalletHasNoPaymentOptions(true);
            return;
          }

          paymentCurrency = paymentOptions[0].paymentCurrency;

          setPaymentOptions(paymentOptions);
          if (depositConfig.allowedPaymentCurrencies?.length === 1) {
            setIsCreatingWalletSession(false);
            setPendingAutoSelectPaymentOption(paymentOptions[0]);
            return;
          }
          navigateToStep("payment_options");
          setIsCreatingWalletSession(false);
          return;
        }

        const depositOwnerAddress = await awaitDepositOwnerAddress(
          chainIdFromPaymentCurrency(paymentCurrency),
        );
        const sessionPromise = depositConfig.widgetSessionId
          ? createSessionFromWidget(glideConfig, {
              sessionId: depositConfig.widgetSessionId,
              account: address,
              paymentCurrency,
              paymentMethod: "wallet",
              preferGaslessPayment: depositConfig.preferGaslessPayment,
              amount:
                depositConfig.mode === "deposit" ||
                depositConfig.mode === "withdraw"
                  ? dep || undefined
                  : undefined,
              widgetConfig: depositConfig.widgetConfig,
              depositOwnerAddress,
            })
          : // @ts-expect-error: createTx return type is not properly typed
            createSession(glideConfig, {
              account: address,
              paymentCurrency,
              paymentMethod: "wallet",
              preferGaslessPayment: depositConfig.preferGaslessPayment,
              paymentAmount:
                depositConfig.mode === "withdraw" ||
                depositConfig.mode === "pay" ||
                depositConfig.mode === "call" ||
                (depositConfig.mode === "deposit" &&
                  depositConfig.hideSettlementCopy)
                  ? dep
                  : undefined,
              metadata: depositConfig.sessionMetadata,
              commissionRates: depositConfig.commissionRates,
              widgetConfig: depositConfig.widgetConfig,
              depositOwnerAddress,
              enableRefundEmails: depositConfig.enableRefundEmails,
              payerEmail: depositConfig.payerEmail,
              stableDepositAddressKey: depositConfig.stableDepositAddressKey,
              routePriority,
              ...tx,
            });

        const routesPromise = listGlideRoutes(glideConfig, {
          paymentCurrency,
          spendCurrency: depositConfig.currency!.on(depositConfig.chain!),
        });

        const [session, routes] = await Promise.all([
          sessionPromise,
          routesPromise,
        ]);

        // Track session created
        widgetAnalytics.sessionCreated(
          session.sessionId,
          "wallet",
          txAmountUSD,
        );

        setGlideSession(session);
        setRoutes(routes);
        setIsUpdatingSession(false);
        setIsCreatingWalletSession(false);

        // Save deposit amount preference when proceeding to review
        await saveLastDepositAmount(dep, depositAmountDenomination);

        navigateToStep("review");
        return session;
      } catch (e) {
        if (e instanceof GlideOverCapacityError) {
          setIsOverCapacity(true);
          widgetAnalytics.errorOccurred("over_capacity", "Service at capacity");
          navigateToStep("deposit_amount", true);
          setIsUpdatingSession(false);
          setIsCreatingWalletSession(false);
          return;
        }
        if (e instanceof InsufficientPaymentAmountError) {
          setIsInsufficientPaymentAmount(true);
          widgetAnalytics.errorOccurred(
            "insufficient_payment",
            "Payment amount too low",
          );
          navigateToStep("deposit_amount", true);
          setIsUpdatingSession(false);
          setIsCreatingWalletSession(false);
          return;
        }

        // Track generic session creation failure
        widgetAnalytics.sessionCreationFailed(String(e));
        throw e;
      }
    },
    [
      depositAmount,
      depositAmountDenomination,
      withdrawDestinationChain,
      depositConfig,
      glideConfig,
      selectedWithdrawCurrency,
      selectedPaymentOption,
      navigateToStep,
      navigateStartOver,
      txAmountUSD,
      isOnrampOnly,
      awaitDepositOwnerAddress,
    ],
  );

  const initCreateSession = useRef(false);
  useEffect(() => {
    if (initCreateSession.current) return;
    if (!isWalletReady) return;
    initCreateSession.current = true;

    if (step !== "creating_session") return;

    // Skip createGlideSession if onramp-only mode (handled by separate useEffect)
    if (isOnrampOnly) return;

    createGlideSession();
  }, [step, createGlideSession, isOnrampOnly, isWalletReady]);

  // Handle refund mode step navigation after session loads
  useEffect(() => {
    if (
      depositConfig.mode === "refund" &&
      refundSession &&
      step === "creating_session"
    ) {
      const targetStep = getRefundModeTargetStep(refundSession, refundToken);

      setFailedSession(refundSession);
      navigateToStep(targetStep, true);
    }
  }, [depositConfig.mode, refundSession, refundToken, step, navigateToStep]);

  // Handle onramp-only mode: create session and navigate to pay_with_onramp
  useEffect(() => {
    if (step === "creating_session" && isOnrampOnly && isWalletReady) {
      const createOnrampSession = async () => {
        try {
          setIsCreatingOnrampSession(true);
          setIsUpdatingSession(true);

          const tx = await createGlideTx(
            { glideConfig, ...depositConfig },
            { amount: depositConfig.amount || "0" },
          );

          const depositOwnerAddress = await awaitDepositOwnerAddress(
            chainIdFromPaymentCurrency(eth.on(base)),
          );
          const session = depositConfig.widgetSessionId
            ? await createSessionFromWidget(glideConfig, {
                sessionId: depositConfig.widgetSessionId,
                paymentCurrency: eth.on(base),
                paymentMethod: "onramp" as PaymentMethod,
                amount:
                  depositConfig.mode === "deposit"
                    ? depositConfig.amount || undefined
                    : undefined,
                widgetConfig: depositConfig.widgetConfig,
                depositOwnerAddress,
              })
            : // @ts-expect-error: createTx return type is not properly typed
              await createSession(glideConfig, {
                paymentCurrency: eth.on(base),
                paymentMethod: "onramp",
                metadata: depositConfig.sessionMetadata,
                commissionRates: depositConfig.commissionRates,
                widgetConfig: depositConfig.widgetConfig,
                enableRefundEmails: depositConfig.enableRefundEmails,
                payerEmail: depositConfig.payerEmail,
                stableDepositAddressKey: depositConfig.stableDepositAddressKey,
                depositOwnerAddress,
                ...tx,
              });

          setGlideSession(session);
          navigateToStep("pay_with_onramp");
          setIsCreatingOnrampSession(false);
          setIsUpdatingSession(false);
        } catch (e) {
          if (e instanceof GlideOverCapacityError) {
            setIsOverCapacity(true);
            setIsCreatingOnrampSession(false);
            setIsUpdatingSession(false);
            return;
          }
          logger.error(`Failed to create onramp session: ${e}`);
          setIsCreatingOnrampSession(false);
          setIsUpdatingSession(false);
          navigateToStep("error");
        }
      };

      createOnrampSession();
    }
  }, [
    step,
    isOnrampOnly,
    isWalletReady,
    depositConfig,
    glideConfig,
    navigateToStep,
    awaitDepositOwnerAddress,
  ]);

  const onChangePaymentOption = async (
    po: PaymentOption,
    paymentMethodOverride?: "wallet" | "transfer" | "coinbase",
    depositAmountOverride?: string,
    // When true, omit stableDepositAddressKey so the backend mints a fresh,
    // non-deterministic deposit address instead of the stable one.
    omitStableDepositAddressKey = false,
  ) => {
    const stableDepositAddressKeyArgs = stableDepositAddressKeyPayload(
      depositConfig.stableDepositAddressKey,
      omitStableDepositAddressKey,
    );

    // amountUSD path: backend computes the token amount
    if (depositConfig.amountUSD && Number(depositConfig.amountUSD) > 0) {
      try {
        setIsUpdatingSession(true);

        const { address } = await getConnectedWallet();

        const sessionPaymentMethod = paymentMethodOverride || paymentMethod;
        if (
          sessionPaymentMethod !== "transfer" &&
          sessionPaymentMethod !== "wallet" &&
          sessionPaymentMethod !== "coinbase_app"
        ) {
          throw new Error("unexpected payment method");
        }
        const createSessionPaymentMethod =
          sessionPaymentMethod === "coinbase_app"
            ? "transfer"
            : sessionPaymentMethod;

        const account =
          createSessionPaymentMethod === "wallet" ? address : undefined;

        const preferGaslessPayment =
          createSessionPaymentMethod === "wallet"
            ? depositConfig.preferGaslessPayment
            : undefined;

        const depositOwnerAddress = await awaitDepositOwnerAddress(
          chainIdFromPaymentCurrency(po.paymentCurrency),
        );
        const session = await createSession(glideConfig, {
          account,
          paymentCurrency: po.paymentCurrency,
          paymentMethod: createSessionPaymentMethod,
          preferGaslessPayment,
          paymentAmountUSD: depositConfig.amountUSD,
          transfer: {
            token: depositConfig.currency!.contractAddressOn!(
              depositConfig.chain!,
            ),
            recipient: depositConfig.recipient!,
            amount: "0",
          },
          chainId: depositConfig.chain!.id,
          metadata: depositConfig.sessionMetadata,
          commissionRates: depositConfig.commissionRates,
          depositOwnerAddress,
          widgetConfig: depositConfig.widgetConfig,
          enableRefundEmails: depositConfig.enableRefundEmails,
          payerEmail: depositConfig.payerEmail,
          ...stableDepositAddressKeyArgs,
        });

        setDepositAmount(session.paymentAmount.toString());
        setGlideSession(session);
        setIsUpdatingSession(false);

        if (
          sessionPaymentMethod === "wallet" ||
          sessionPaymentMethod === "coinbase_app"
        ) {
          // Save deposit amount preference when proceeding to review
          await saveLastDepositAmount(
            session.paymentAmount.toString(),
            depositAmountDenomination,
          );
          navigateToStep("review");
        }
      } catch (e) {
        if (e instanceof GlideOverCapacityError) {
          setIsOverCapacity(true);
          setIsUpdatingSession(false);
          return;
        }
        if (e instanceof InsufficientPaymentAmountError) {
          setIsInsufficientPaymentAmount(true);
          setIsUpdatingSession(false);
          return;
        }
        throw e;
      }
      return;
    }

    const sessionDepositAmount = depositAmountOverride || depositAmount;

    if (!sessionDepositAmount) {
      throw new Error("depositAmount not set");
    }

    const sessionPaymentMethod = paymentMethodOverride || paymentMethod;

    if (
      sessionPaymentMethod !== "transfer" &&
      sessionPaymentMethod !== "wallet" &&
      sessionPaymentMethod !== "coinbase_app"
    ) {
      throw new Error("unexpected payment method");
    }

    if (sessionPaymentMethod === "coinbase_app" && !coinbaseAccessToken) {
      throw new Error("coinbaseAccessToken not set");
    }
    const createSessionPaymentMethod =
      sessionPaymentMethod === "coinbase_app"
        ? "transfer"
        : sessionPaymentMethod;

    try {
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: sessionDepositAmount },
      );
      const { address } = await getConnectedWallet();

      const account =
        createSessionPaymentMethod === "wallet" ? address : undefined;

      const preferGaslessPayment =
        createSessionPaymentMethod === "wallet"
          ? depositConfig.preferGaslessPayment
          : undefined;

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(po.paymentCurrency),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            account,
            paymentCurrency: po.paymentCurrency,
            paymentMethod: createSessionPaymentMethod,
            preferGaslessPayment,
            omitStableDepositAddressKey:
              omitStableDepositAddressKey || undefined,
            amount:
              depositConfig.mode === "deposit"
                ? sessionDepositAmount || undefined
                : undefined,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            account,
            paymentCurrency: po.paymentCurrency,
            paymentMethod: createSessionPaymentMethod,
            preferGaslessPayment,
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            allowArbitraryDeposit:
              !depositConfig.hasFixedAmount &&
              (depositConfig.mode === "deposit" ||
                depositConfig.mode === "pay") &&
              createSessionPaymentMethod === "transfer",
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            ...stableDepositAddressKeyArgs,
            depositOwnerAddress,
            ...tx,
          });

      if (
        sessionPaymentMethod === "transfer" &&
        session.paymentChainId === bitcoin.id &&
        !depositConfig.excludeChainIds?.includes(lightning.id)
      ) {
        const lightningDepositOwnerAddress = await awaitDepositOwnerAddress(
          chainIdFromPaymentCurrency(btc.on(lightning)),
        );
        const bitcoinLightningSession = depositConfig.widgetSessionId
          ? await createSessionFromWidget(glideConfig, {
              sessionId: depositConfig.widgetSessionId,
              paymentCurrency: btc.on(lightning),
              paymentMethod: "transfer",
              omitStableDepositAddressKey:
                omitStableDepositAddressKey || undefined,
              amount:
                depositConfig.mode === "deposit"
                  ? sessionDepositAmount || undefined
                  : undefined,
              widgetConfig: depositConfig.widgetConfig,
              depositOwnerAddress: lightningDepositOwnerAddress,
            })
          : // @ts-expect-error: createTx return type is not properly typed
            await createSession(glideConfig, {
              paymentCurrency: btc.on(lightning),
              paymentMethod: "transfer",
              metadata: depositConfig.sessionMetadata,
              commissionRates: depositConfig.commissionRates,
              allowArbitraryDeposit:
                !depositConfig.hasFixedAmount &&
                (depositConfig.mode === "deposit" ||
                  depositConfig.mode === "pay"),
              widgetConfig: depositConfig.widgetConfig,
              enableRefundEmails: depositConfig.enableRefundEmails,
              payerEmail: depositConfig.payerEmail,
              ...stableDepositAddressKeyArgs,
              depositOwnerAddress: lightningDepositOwnerAddress,
              ...tx,
            });

        setBitcoinLightningSession(bitcoinLightningSession);
      }

      setIsUpdatingSession(false);
      setGlideSession(session);

      if (
        sessionPaymentMethod === "transfer" &&
        step === "transfer_choose_chain" &&
        (depositConfig.mode === "deposit" || depositConfig.mode === "withdraw")
      ) {
        navigateToStep("deposit_amount");
      } else if (sessionPaymentMethod === "transfer") {
        navigateToStep("transfer");
      }

      if (
        sessionPaymentMethod === "wallet" ||
        sessionPaymentMethod === "coinbase_app"
      ) {
        // Save deposit amount preference when proceeding to review
        await saveLastDepositAmount(
          sessionDepositAmount,
          depositAmountDenomination,
        );
        navigateToStep("review");
      }
    } catch (e) {
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsUpdatingSession(false);
        return;
      }
      throw e;
    }
  };

  const onChangePaymentOptionRef = useRef(onChangePaymentOption);
  onChangePaymentOptionRef.current = onChangePaymentOption;
  useEffect(() => {
    if (!pendingAutoSelectPaymentOption) return;
    setPendingAutoSelectPaymentOption(null);
    onChangePaymentOptionRef.current(pendingAutoSelectPaymentOption);
  }, [pendingAutoSelectPaymentOption]);

  const onBuyChooseChain = async (chain: Chain) => {
    depositConfig.setChain(chain);

    const currencies = await listSupportedCurrencies(glideConfig, {
      chainId: chain.id,
      all: true,
    });

    setCurrencyOptions(currencies);
    navigateToStep("buy_token_selector");
  };

  const onTransferChooseChain = async (chain: Chain) => {
    const tx = await createGlideTx(
      { glideConfig, ...depositConfig },
      { amount: "1" },
    );

    if (chain.id === bitcoin.id || chain.id === bitcoinTestnet.id) {
      // @ts-expect-error: createTx return type is not properly typed
      const paymentOption = await estimatePaymentAmount(glideConfig, {
        paymentCurrency: btc.on(chain),
        ...tx,
      });

      setPaymentMethod("transfer");
      setSelectedPaymentOption(paymentOption);
      setPaymentOptions([paymentOption]);

      if (depositConfig.mode === "deposit") {
        navigateToStep("deposit_amount");
        return;
      }

      return await onChangePaymentOption(paymentOption, "transfer");
    }

    let paymentCurrencies: Currency[] | undefined;
    if (depositConfig.allowedPaymentCurrencies?.length) {
      paymentCurrencies = await getAllowedPaymentCurrencies(
        glideConfig,
        depositConfig.allowedPaymentCurrencies,
        [chain.id],
      );
    }

    // @ts-expect-error: createTx return type is not properly typed
    const paymentOptions = await listPaymentOptions(glideConfig, {
      // fixme: add account + includeInsufficientBalanceOptions ?
      paymentChainIds: [chain.id],
      commissionRates: depositConfig.commissionRates,
      excludeChainIds: depositConfig.excludeChainIds,
      excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
      ...(paymentCurrencies && { paymentCurrencies }),
      ...tx,
    });

    setPaymentMethod("transfer");
    setPaymentOptions(paymentOptions);
    navigateToStep("payment_options");
  };

  const coinbaseLoginPopupIntervalRef = useRef<NodeJS.Timeout | undefined>(
    undefined,
  );
  const hasPostedOpenMessage = useRef(false);

  const createCoinbaseOAuthToken = async () => {
    setIsCreatingCoinbaseAppSession(true);

    const oauthConfig = {
      glideUsersBaseUrl:
        import.meta.env.VITE_GLIDE_USERS_BASE_URL ||
        "https://mainnet.walletkit.com/users/",
      glideUsersProjectId:
        import.meta.env.VITE_GLIDE_USERS_PROJECT_ID ||
        "7b345153-c2db-4e33-b01c-02712b55d159",
      coinbaseAppRedirectUri:
        import.meta.env.VITE_COINBASE_APP_REDIRECT_URI ||
        "https://mainnet.walletkit.com/users/oauth/coinbase-callback",
      coinbaseAppClientId:
        import.meta.env.VITE_COINBASE_APP_CLIENT_ID ||
        "a373e873-b514-4413-bc4d-d7f9eab47cbd",
    };

    const width = 500;
    const height = 760;
    const top = Math.max((window.innerHeight - height) / 2, 0);
    const left = Math.max(window.innerWidth - width - 60, 0);

    const w = window.open(
      "",
      "popup",
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );

    let sessionChallengeCode = "";

    setTimeout(async () => {
      if (!w) {
        return;
      }

      const url = oauthConfig.glideUsersBaseUrl + "create-session-challenge";

      const challengeResp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Glide-Project-Id": oauthConfig.glideUsersProjectId,
        },
        body: JSON.stringify({
          oauth_provider: "coinbase",
        }),
      }).then((response) => response.json());

      const state = btoa(
        JSON.stringify({
          project_id: oauthConfig.glideUsersProjectId,
          session_challenge_code: challengeResp.code,
          redirect_uri: oauthConfig.coinbaseAppRedirectUri,
        }),
      );

      sessionChallengeCode = challengeResp.code;

      w.location.href = `https://login.coinbase.com/oauth2/auth?response_type=code&client_id=${oauthConfig.coinbaseAppClientId}&redirect_uri=${oauthConfig.coinbaseAppRedirectUri}&scope=wallet:transactions:send+wallet:accounts:read&state=${state}`;
    }, 500);

    coinbaseLoginPopupIntervalRef.current = setInterval(async () => {
      if (!w || w.closed) {
        clearInterval(coinbaseLoginPopupIntervalRef.current);
        coinbaseLoginPopupIntervalRef.current = undefined;

        if (!sessionChallengeCode) return;

        const url = oauthConfig.glideUsersBaseUrl + "verify-login";

        const { access_token: accessToken } = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Glide-Project-Id": oauthConfig.glideUsersProjectId,
          },
          body: JSON.stringify({
            session_challenge_code: sessionChallengeCode,
          }),
        }).then((response) => response.json());

        setCoinbaseAccessToken(accessToken);
        setPaymentMethod("coinbase_app");

        const tx = await createGlideTx(
          { glideConfig, ...depositConfig },
          { amount: depositAmount },
        );

        // @ts-expect-error: createTx return type is not properly typed
        const paymentOptions = await listPaymentOptions(glideConfig, {
          paymentChainIds: depositConfig.chainIds?.length
            ? depositConfig.chainIds
            : undefined,
          coinbaseAccessToken: accessToken,
          includeInsufficientBalanceOptions: true,
          commissionRates: depositConfig.commissionRates,
          excludeChainIds: depositConfig.excludeChainIds,
          excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
          ...tx,
        });

        if (!paymentOptions.length) {
          setPaymentOptions([]);
          setGlideSession(undefined);
          navigateStartOver();
          setWalletHasNoPaymentOptions(true);
          setIsCreatingCoinbaseAppSession(false);
          return;
        }

        setPaymentOptions(paymentOptions);
        setSelectedPaymentOption(paymentOptions[0]);
        navigateToStep("deposit_amount");
        setIsCreatingCoinbaseAppSession(false);
        setWalletHasNoPaymentOptions(false);
      }
    }, 1000);
  };

  const loadWalletPaymentOptions = useCallback(
    async (shouldNavigate: boolean = true) => {
      const { address, availableChainIds, isConnected } =
        await getConnectedWallet();

      if (!isConnected || !address) {
        return;
      }

      setPaymentMethod("wallet");
      setIsLoadingPaymentOptions(true);

      try {
        // For no amount flow, we need to create a minimal tx to get payment options
        // Use a placeholder amount of "1" since we're not actually creating a session yet
        const tx = await createGlideTx(
          { glideConfig, ...depositConfig },
          { amount: "1" },
        );

        // Filter out deposit-only chains that don't support wallet payments
        const depositOnlyChains = ["eip155:41454", "eip155:8453001"]; // HyperEVM, Hypercore
        const walletSupportedChains = availableChainIds?.filter(
          (chainId) => !depositOnlyChains.includes(chainId),
        );

        const paymentChainIds = filterPaymentChainIds(
          depositConfig.chainIds,
          walletSupportedChains,
          depositConfig.excludeChainIds,
        );

        let paymentCurrencies: Currency[] | undefined;
        if (depositConfig.allowedPaymentCurrencies?.length) {
          paymentCurrencies = await getAllowedPaymentCurrencies(
            glideConfig,
            depositConfig.allowedPaymentCurrencies,
            paymentChainIds || [],
          );
        }

        // @ts-expect-error: createTx return type is not properly typed
        const paymentOptions = await listPaymentOptions(glideConfig, {
          paymentChainIds,
          account: address,
          includeInsufficientBalanceOptions: true,
          commissionRates: depositConfig.commissionRates,
          excludeChainIds: depositConfig.excludeChainIds,
          excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
          ...(paymentCurrencies && { paymentCurrencies }),
          ...tx,
        });

        if (!paymentOptions.length) {
          setPaymentOptions([]);
          setGlideSession(undefined);
          setIsUpdatingSession(false);
          if (shouldNavigate) {
            navigateToStep("funding_source");
          }
          setWalletHasNoPaymentOptions(true);
          setIsLoadingPaymentOptions(false);
          return;
        }

        setIsLoadingPaymentOptions(false);
        setPaymentOptions(paymentOptions);

        // Check if we have a saved token selection and can skip the token picker
        const savedSelection = loadTokenSelection();
        let selectedOption = paymentOptions[0];

        if (savedSelection && shouldNavigate) {
          // Try to find a payment option matching the saved currency
          // Match by exact ID or by symbol (more flexible)
          const matchingOption = paymentOptions.find(
            (po) =>
              po.paymentCurrency === savedSelection.currencyId ||
              po.currencySymbol === savedSelection.currencySymbol,
          );

          if (matchingOption) {
            // Found matching option - select it and go directly to amount screen
            selectedOption = matchingOption;
            setSelectedPaymentOption(matchingOption);
            navigateToStep("deposit_amount");
            return;
          }
        }

        // No saved selection or no match found - show token picker
        setSelectedPaymentOption(selectedOption);
        if (shouldNavigate) {
          navigateToStep("payment_options");
        }
      } catch (error) {
        setIsCreatingWalletSession(false);
        setIsLoadingPaymentOptions(false);
        throw error;
      }
    },
    [glideConfig, depositConfig, navigateToStep],
  );

  // Preload payment options when wallet is connected on funding source screen
  useEffect(() => {
    const preloadPaymentOptions = async () => {
      if (
        step === "funding_source" &&
        isConnected &&
        paymentOptions === undefined &&
        !isLoadingPaymentOptions &&
        !isCreatingWalletSession &&
        !isLoadingTransferCurrencies
      ) {
        try {
          await loadWalletPaymentOptions(false); // Don't navigate
        } catch (error) {
          logger.error(`Failed to preload payment options: ${error}`);
        }
      }
    };

    preloadPaymentOptions();
  }, [
    step,
    isConnected,
    paymentOptions,
    isLoadingPaymentOptions,
    isCreatingWalletSession,
    isLoadingTransferCurrencies,
    loadWalletPaymentOptions,
  ]);

  const payWithWallet = async ({ recipient }: { recipient?: string } = {}) => {
    const { address, availableChainIds, isConnected } =
      await getConnectedWallet();

    if (!isConnected || !address) {
      logger.error("payWithWallet called but wallet is not connected");
      navigateToStep("connect_wallet");
      return;
    }

    setPaymentMethod("wallet");
    setIsCreatingWalletSession(true);

    try {
      if (depositConfig.mode === "withdraw" && withdrawDestinationChain) {
        const currencies = await listSupportedCurrencies(glideConfig, {
          chainId: withdrawDestinationChain.id,
        });

        setIsCreatingWalletSession(false);
        setSelectedWithdrawCurrency(
          depositConfig.currency
            ? currencies.find(
                (c) => c.symbol === depositConfig.currency!.symbol,
              ) || currencies[0]
            : currencies[0],
        );
        setWithdrawCurrencies(currencies);
        navigateToStep("deposit_amount");
      } else {
        const tx = await createGlideTx(
          { glideConfig, ...depositConfig },
          { recipient },
        );

        // Filter out deposit-only chains that don't support wallet payments
        const depositOnlyChains = ["eip155:41454", "eip155:8453001"]; // HyperEVM, Hypercore
        const walletSupportedChains = availableChainIds?.filter(
          (chainId) => !depositOnlyChains.includes(chainId),
        );

        const paymentChainIds = filterPaymentChainIds(
          depositConfig.chainIds,
          walletSupportedChains,
        );

        let paymentCurrencies: Currency[] | undefined;
        if (depositConfig.allowedPaymentCurrencies?.length) {
          paymentCurrencies = await getAllowedPaymentCurrencies(
            glideConfig,
            depositConfig.allowedPaymentCurrencies,
            paymentChainIds || [],
          );
        }

        // @ts-expect-error: createTx return type is not properly typed
        const paymentOptions = await listPaymentOptions(glideConfig, {
          paymentChainIds,
          account: address,
          includeInsufficientBalanceOptions: true,
          commissionRates: depositConfig.commissionRates,
          excludeChainIds: depositConfig.excludeChainIds,
          excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
          ...(paymentCurrencies && { paymentCurrencies }),
          ...tx,
        });

        if (!paymentOptions.length) {
          setPaymentOptions([]);
          setGlideSession(undefined);
          setIsUpdatingSession(false);
          navigateToStep("funding_source");
          setWalletHasNoPaymentOptions(true);
          setIsCreatingWalletSession(false);
          return;
        }

        setIsCreatingWalletSession(false);
        setSelectedPaymentOption(paymentOptions[0]);
        setPaymentOptions(paymentOptions);
        navigateToStep("payment_options");
      }
    } catch (e) {
      logger.error(`Failed to load wallet payment options: ${e}`);
      widgetAnalytics.sessionCreationFailed(String(e));
      setIsCreatingWalletSession(false);
      setIsUpdatingSession(false);
      if (depositConfig.mode !== "withdraw") {
        // Non-undefined so the funding_source preload effect doesn't retry forever.
        setPaymentOptions([]);
        setFundingSourceError(
          "We couldn\u2019t load your wallet balances. Please try another payment method.",
        );
        navigateToStep("funding_source");
      }
    }
  };

  const payWithMoonPay = async (depositAmount: string) => {
    try {
      setIsCreatingMoonpayOnrampSession(true);
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: depositAmount },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(eth.on(ethereum)),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            paymentCurrency: eth.on(ethereum),
            paymentMethod: "moonpay",
            amount:
              depositConfig.mode === "deposit"
                ? depositAmount || undefined
                : undefined,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            paymentCurrency: eth.on(ethereum),
            paymentMethod: "moonpay",
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            depositOwnerAddress,
            ...tx,
          });

      setGlideSession(session);

      navigateToStep("pay_with_moonpay");
      setIsCreatingMoonpayOnrampSession(false);
    } catch (e) {
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsCreatingMoonpayOnrampSession(false);
        setIsUpdatingSession(false);
        return;
      }
      throw e;
    }
  };

  const payWithInterac = async (depositAmount: string) => {
    try {
      setIsCreatingInteracSession(true);
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: depositAmount },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(usdc.on(sx)),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            paymentCurrency: usdc.on(sx),
            paymentMethod: "transfer",
            amount:
              depositConfig.mode === "deposit"
                ? depositAmount || undefined
                : undefined,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            paymentCurrency: usdc.on(sx),
            paymentMethod: "transfer",
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            allowArbitraryDeposit:
              !depositConfig.hasFixedAmount &&
              (depositConfig.mode === "deposit" ||
                depositConfig.mode === "pay"),
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            depositOwnerAddress,
            ...tx,
          });

      setGlideSession(session);

      navigateToStep("pay_with_interac");
      setIsCreatingInteracSession(false);
    } catch (e) {
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsCreatingInteracSession(false);
        setIsUpdatingSession(false);
        return;
      }
      throw e;
    }
  };

  const payWithOnramper = async () => {
    const width = 500;
    const height = 760;
    const top = Math.max((window.innerHeight - height) / 2, 0);
    const left = Math.max(window.innerWidth - width - 60, 0);
    const popup = window.open(
      "",
      "onramper_popup",
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
    onramperPopupRef.current = popup;

    try {
      setIsCreatingOnramperSession(true);
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: "0" },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(usdc.on(base)),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            paymentCurrency: usdc.on(base),
            paymentMethod: "onramper" as PaymentMethod,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            paymentCurrency: usdc.on(base),
            paymentMethod: "onramper" as PaymentMethod,
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            allowArbitraryDeposit: true,
            depositOwnerAddress,
            ...tx,
          });

      if (popup && session.redirectUrl) {
        popup.location.href = session.redirectUrl;
      }

      setGlideSession(session);
      navigateToStep("pay_with_onramper");
      setIsCreatingOnramperSession(false);
    } catch (e) {
      if (popup) popup.close();
      onramperPopupRef.current = null;
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsCreatingOnramperSession(false);
        setIsUpdatingSession(false);
        return;
      }
      throw e;
    }
  };

  const payWithOnramp = async (depositAmount: string) => {
    if (!depositAmount) return;

    try {
      setIsCreatingOnrampSession(true);
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: depositAmount },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(eth.on(base)),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            paymentCurrency: eth.on(base),
            paymentMethod: "onramp",
            amount:
              depositConfig.mode === "deposit"
                ? depositAmount || undefined
                : undefined,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            paymentCurrency: eth.on(base),
            paymentMethod: "onramp",
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            depositOwnerAddress,
            ...tx,
          });

      setGlideSession(session);
      navigateToStep("pay_with_onramp");
      setIsCreatingOnrampSession(false);
    } catch (e) {
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsCreatingOnrampSession(false);
        setIsUpdatingSession(false);
        return;
      }
      throw e;
    }
  };

  const payWithCoinbaseOnramp = async (depositAmount: string) => {
    if (!depositAmount) return;

    try {
      setIsCreatingCoinbaseOnrampSession(true);
      setIsUpdatingSession(true);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        { amount: depositAmount },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        chainIdFromPaymentCurrency(eth.on(base)),
      );
      const session = depositConfig.widgetSessionId
        ? await createSessionFromWidget(glideConfig, {
            sessionId: depositConfig.widgetSessionId,
            paymentCurrency: eth.on(base),
            paymentMethod: "coinbase_onramp",
            amount:
              depositConfig.mode === "deposit"
                ? depositAmount || undefined
                : undefined,
            widgetConfig: depositConfig.widgetConfig,
            depositOwnerAddress,
          })
        : // @ts-expect-error: createTx return type is not properly typed
          await createSession(glideConfig, {
            paymentCurrency: eth.on(base),
            paymentMethod: "coinbase_onramp",
            metadata: depositConfig.sessionMetadata,
            commissionRates: depositConfig.commissionRates,
            widgetConfig: depositConfig.widgetConfig,
            enableRefundEmails: depositConfig.enableRefundEmails,
            payerEmail: depositConfig.payerEmail,
            stableDepositAddressKey: depositConfig.stableDepositAddressKey,
            depositOwnerAddress,
            ...tx,
          });

      setGlideSession(session);
      navigateToStep("pay_with_coinbase_onramp");
      setIsCreatingCoinbaseOnrampSession(false);
    } catch (e) {
      if (e instanceof GlideOverCapacityError) {
        setIsOverCapacity(true);
        setIsCreatingCoinbaseOnrampSession(false);
        setIsUpdatingSession(false);
        return;
      }
      console.error("Failed to create Coinbase Onramp session:", e);
      setIsCreatingCoinbaseOnrampSession(false);
      setIsUpdatingSession(false);
      setFundingSourceError(
        "Coinbase is currently unavailable. Please try another payment method.",
      );
      navigateToStep("funding_source");
    }
  };

  const closeModal = useCallback(() => {
    if (!depositConfig.embed) return;

    // Track modal closed (without transaction completion)
    widgetAnalytics.modalClosed(false);

    onClose();

    setTimeout(() => {
      window.parent?.postMessage({ type: "close" }, "*");
    }, 500);
  }, [depositConfig.embed, onClose]);

  const postSuccessMessage = useCallback(
    (session: Session) => {
      // Track transaction completion
      widgetAnalytics.transactionCompleted(
        session.sessionId,
        session.sponsoredTransactionHash || "",
        paymentMethod || "unknown",
        txAmountUSD,
      );

      if ("ReactNativeWebView" in window) {
        (window.ReactNativeWebView as Window).postMessage(
          JSON.stringify({
            type: "success",
            session,
          }),
        );
      }

      if (depositConfig.embed) {
        window.parent?.postMessage(
          {
            type: "success",
            hash: session.sponsoredTransactionHash!,
            session,
          },
          "*",
        );
      }
    },
    [depositConfig.embed, paymentMethod, txAmountUSD],
  );

  const onTransferSuccess = useCallback(
    (session: Session) => {
      rememberRefundToken(session);
      setGlideSession(session);
      if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
        closeModal();
      } else {
        navigateToStep("success");
      }
      postSuccessMessage(session);
    },
    [
      closeModal,
      depositConfig.autoCloseOnSuccess,
      depositConfig.embed,
      navigateToStep,
      postSuccessMessage,
      rememberRefundToken,
    ],
  );

  const resetDepositAmountErrors = React.useCallback(() => {
    setIsOverCapacity(false);
    setIsInsufficientPaymentAmount(false);
  }, []);

  const handleInsufficientPaymentAmount = useCallback(
    (session: Session) => {
      rememberRefundToken(session);
      setFailedSession(session);
      const sessionRefundToken = (session as Record<string, unknown>)
        .refundToken;
      const effectiveRefundToken =
        typeof sessionRefundToken === "string" && sessionRefundToken
          ? sessionRefundToken
          : refundToken;
      navigateToStep(
        effectiveRefundToken ? "insufficient_payment_amount" : "error",
      );
    },
    [navigateToStep, refundToken, rememberRefundToken],
  );

  const handleRequestRefund = useCallback(
    (session: Session) => {
      rememberRefundToken(session);
      setFailedSession(session);
      const sessionRefundToken = (session as Record<string, unknown>)
        .refundToken;
      const effectiveRefundToken =
        typeof sessionRefundToken === "string" && sessionRefundToken
          ? sessionRefundToken
          : refundToken;
      navigateToStep(effectiveRefundToken ? "confirm_refund" : "error");
    },
    [navigateToStep, refundToken, rememberRefundToken],
  );

  const handleOverpaymentRefund = useCallback(
    (session: Session) => {
      rememberRefundToken(session);
      const sessionRefundToken = (session as Record<string, unknown>)
        .refundToken;
      const effectiveRefundToken =
        typeof sessionRefundToken === "string" && sessionRefundToken
          ? sessionRefundToken
          : refundToken;
      const requiredAmount = Number(session.paymentAmountUSD);
      const paidAmount = Number(session.actualPaymentAmountUSD);
      const deltaAmount = paidAmount - requiredAmount;
      logger.info(
        `[OverpaymentFlow][Modal] sessionId=${session.sessionId} status=${session.paymentStatus} requiredUSD=${requiredAmount} paidUSD=${paidAmount} deltaUSD=${deltaAmount} refundTokenPresent=${Boolean(effectiveRefundToken)} navigating=${effectiveRefundToken ? "excess_payment_refund" : "error"}`,
      );
      setFailedSession(session);
      navigateToStep(effectiveRefundToken ? "excess_payment_refund" : "error");
    },
    [navigateToStep, refundToken, rememberRefundToken],
  );

  const isBitcoinMainnetSession = glideSession?.paymentChainId === bitcoin.id;

  // Handle multichain session creation
  const handleMultichainSessionCreated = useCallback(
    (chainType: "evm" | "solana" | "bitcoin" | "tron", session: Session) => {
      switch (chainType) {
        case "evm":
          setMultichainEvmSession(session);
          break;
        case "solana":
          setMultichainSolanaSession(session);
          break;
        case "bitcoin":
          setMultichainBitcoinSession(session);
          break;
        case "tron":
          setMultichainTronSession(session);
          break;
      }
    },
    [],
  );

  const handleMultichainLightningSessionCreated = useCallback(
    (session: Session) => {
      setMultichainBitcoinLightningSession(session);
    },
    [],
  );

  const allowedMultichainChains = getAllowedMultichainChains(
    glideConfig.chains,
    {
      destinationIsTestnet: depositConfig.chain?.isTestnet,
      chainIds: depositConfig.chainIds,
      excludeChainIds: depositConfig.excludeChainIds,
      excludeBitcoin: isBitcoinBelowMinimum,
    },
  );

  const getCurrenciesWithSameSymbol = useCallback(
    (currencies: Currency[], currency: Currency) =>
      currencies.filter((candidate) => candidate.symbol === currency.symbol),
    [],
  );

  // Create multichain session for a specific chain type
  const createMultichainSession = useCallback(
    async (
      chainType: MultichainChainType,
      specificChain?: Chain,
      currency?: Currency,
      // When true, omit stableDepositAddressKey so the backend mints a fresh address.
      omitStableDepositAddressKey = false,
    ) => {
      const stableDepositAddressKeyArgs = stableDepositAddressKeyPayload(
        depositConfig.stableDepositAddressKey,
        omitStableDepositAddressKey,
      );

      try {
        const currencyToUse = currency || selectedMultichainCurrency;
        let paymentCurrency;

        // If currencyToUse and specificChain exist, use them to get accurate payment amount for that specific token
        // Otherwise use default currencies (for deposit mode with arbitrary amounts)
        if (currencyToUse && specificChain) {
          // Use the specific chain provided by the selection strategy.
          paymentCurrency = currencyToUse.on(specificChain);
        } else if (currencyToUse) {
          // Fallback: find a chain for this currency based on chainType
          const currencyChain = findSupportedChainsForCurrency(
            currencyToUse,
            allowedMultichainChains,
            chainType,
          ).find(
            (chain) => !(chainType === "bitcoin" && chain.id === lightning.id),
          );

          if (currencyChain) {
            paymentCurrency = currencyToUse.on(currencyChain);
          } else {
            throw new Error(
              `No allowed chain found for currency ${currencyToUse.id} on ${chainType}`,
            );
          }
        } else {
          // No specific currency selected: pick the first allowed chain for the chosen type.
          const fallbackChain = allowedMultichainChains.find((chain) => {
            const isMatchingType = matchesMultichainType(chain.id, chainType);
            if (!isMatchingType) {
              return false;
            }
            return !(chainType === "bitcoin" && chain.id === lightning.id);
          });

          if (!fallbackChain) {
            throw new Error(`No allowed chain found for ${chainType}`);
          }

          switch (chainType) {
            case "evm":
              paymentCurrency = eth.on(fallbackChain);
              break;
            case "solana":
              paymentCurrency = sol.on(fallbackChain);
              break;
            case "bitcoin":
              paymentCurrency = btc.on(fallbackChain);
              break;
            case "tron":
              paymentCurrency = usdt.on(fallbackChain);
              break;
            default:
              throw new Error(`Unsupported chain: ${chainType}`);
          }
        }

        // Create transaction for this specific chain and currency
        // When amountUSD is set, use "0" as placeholder (backend computes the real amount)
        const tx = await createGlideTx(
          { glideConfig, ...depositConfig },
          {
            amount:
              depositConfig.amount && Number(depositConfig.amount) > 0
                ? depositConfig.amount
                : depositConfig.amountUSD
                  ? "0"
                  : "1",
          },
        );

        const depositOwnerAddress = await awaitDepositOwnerAddress(
          chainIdFromPaymentCurrency(paymentCurrency),
        );
        const session = depositConfig.widgetSessionId
          ? await createSessionFromWidget(glideConfig, {
              sessionId: depositConfig.widgetSessionId,
              paymentCurrency,
              paymentMethod: "transfer",
              omitStableDepositAddressKey:
                omitStableDepositAddressKey || undefined,
              amount:
                depositConfig.mode === "deposit"
                  ? depositConfig.amount || undefined
                  : undefined,
              widgetConfig: depositConfig.widgetConfig,
              depositOwnerAddress,
            })
          : // @ts-expect-error: createTx return type is not properly typed
            await createSession(glideConfig, {
              paymentCurrency,
              paymentMethod: "transfer",
              paymentAmountUSD: depositConfig.amountUSD,
              metadata: depositConfig.sessionMetadata,
              depositOwnerAddress,
              commissionRates: depositConfig.commissionRates,
              allowArbitraryDeposit:
                !depositConfig.hasFixedAmount &&
                (depositConfig.mode === "deposit" ||
                  depositConfig.mode === "pay"),
              widgetConfig: depositConfig.widgetConfig,
              enableRefundEmails: depositConfig.enableRefundEmails,
              payerEmail: depositConfig.payerEmail,
              ...stableDepositAddressKeyArgs,
              ...tx,
            });

        // Save session to state via callback
        handleMultichainSessionCreated(chainType, session);

        // For Bitcoin, also create Lightning session in background if not excluded
        if (
          chainType === "bitcoin" &&
          !depositConfig.excludeChainIds?.includes(lightning.id) &&
          (!depositConfig.chainIds?.length ||
            depositConfig.chainIds.includes(lightning.id)) &&
          !isBitcoinBelowMinimum
        ) {
          // Don't await - let Lightning load in background
          (async () => {
            try {
              const lightningCurrency = currencyToUse
                ? currencyToUse.on(lightning)
                : btc.on(lightning);

              const lightningSession = depositConfig.widgetSessionId
                ? await createSessionFromWidget(glideConfig, {
                    sessionId: depositConfig.widgetSessionId,
                    paymentCurrency: lightningCurrency,
                    paymentMethod: "transfer",
                    omitStableDepositAddressKey:
                      omitStableDepositAddressKey || undefined,
                    amount:
                      depositConfig.mode === "deposit"
                        ? depositConfig.amount || undefined
                        : undefined,
                    widgetConfig: depositConfig.widgetConfig,
                  })
                : // @ts-expect-error: createTx return type is not properly typed
                  await createSession(glideConfig, {
                    paymentCurrency: lightningCurrency,
                    paymentMethod: "transfer",
                    paymentAmountUSD: depositConfig.amountUSD,
                    metadata: depositConfig.sessionMetadata,
                    commissionRates: depositConfig.commissionRates,
                    allowArbitraryDeposit:
                      !depositConfig.hasFixedAmount &&
                      (depositConfig.mode === "deposit" ||
                        depositConfig.mode === "pay"),
                    widgetConfig: depositConfig.widgetConfig,
                    enableRefundEmails: depositConfig.enableRefundEmails,
                    payerEmail: depositConfig.payerEmail,
                    ...stableDepositAddressKeyArgs,
                    ...tx,
                  });

              handleMultichainLightningSessionCreated(lightningSession);
            } catch (e) {
              logger.error(`Failed to create Lightning session: ${e}`);
            }
          })();
        }

        return session;
      } catch (error) {
        logger.error(`Error creating ${chainType} session: ${error}`);
        throw error;
      }
    },
    [
      allowedMultichainChains,
      depositConfig,
      glideConfig,
      selectedMultichainCurrency,
      isBitcoinBelowMinimum,
      handleMultichainSessionCreated,
      handleMultichainLightningSessionCreated,
      awaitDepositOwnerAddress,
    ],
  );

  const restoreSavedMultichainTransferSelection = useCallback(
    async (
      processed: Currency[],
      savedSelection: Pick<
        SavedTokenSelection,
        "currencyId" | "chainType" | "chainId"
      >,
    ): Promise<boolean> => {
      const savedCurrency = processed.find(
        (currency) => currency.id === savedSelection.currencyId,
      );

      if (!savedCurrency) {
        return false;
      }

      const validChains = findSupportedChainsForCurrency(
        savedCurrency,
        allowedMultichainChains,
        savedSelection.chainType,
      );
      const specificChain =
        (savedSelection.chainId
          ? validChains.find((chain) => chain.id === savedSelection.chainId)
          : undefined) || validChains[0];

      if (!specificChain) {
        return false;
      }

      setSelectedMultichainCurrency(savedCurrency);
      setSelectedMultichainCurrencies(
        getCurrenciesWithSameSymbol(processed, savedCurrency),
      );
      setSelectedMultichainType(savedSelection.chainType);

      try {
        await createMultichainSession(
          savedSelection.chainType,
          specificChain,
          savedCurrency,
        );
        return true;
      } catch (error) {
        logger.error(`Failed to create session with saved selection: ${error}`);
        return false;
      }
    },
    [
      allowedMultichainChains,
      createMultichainSession,
      getCurrenciesWithSameSymbol,
    ],
  );

  const autoSelectFallbackMultichainTransferCurrency = useCallback(
    async (processed: Currency[]): Promise<boolean> => {
      if (!processed.length) {
        return false;
      }

      const preferredSymbols = ["USDC", "USDT", "ETH"];
      const fallbackCandidates = [
        ...preferredSymbols.flatMap((symbol) =>
          processed.filter(
            (currency) => currency.symbol.toUpperCase() === symbol,
          ),
        ),
        ...processed,
      ];
      const seenCurrencyIds = new Set<string>();

      for (const fallbackCurrency of fallbackCandidates) {
        if (seenCurrencyIds.has(fallbackCurrency.id)) {
          continue;
        }
        seenCurrencyIds.add(fallbackCurrency.id);

        const selection = findFirstSupportedChainSelection(
          fallbackCurrency,
          allowedMultichainChains,
        );

        if (!selection) {
          continue;
        }

        setSelectedMultichainCurrency(fallbackCurrency);
        setSelectedMultichainCurrencies(
          getCurrenciesWithSameSymbol(processed, fallbackCurrency),
        );
        setSelectedMultichainType(selection.chainType);

        try {
          await createMultichainSession(
            selection.chainType,
            selection.chain,
            fallbackCurrency,
          );
          return true;
        } catch (error) {
          logger.error(`Failed to auto-select fallback currency: ${error}`);
        }
      }

      return false;
    },
    [
      allowedMultichainChains,
      createMultichainSession,
      getCurrenciesWithSameSymbol,
    ],
  );

  const loadTransferCurrenciesAndMaybeSelect = useCallback(async () => {
    const currencies = await listSupportedCurrencies(glideConfig, {
      all: false,
    });
    const transferAllowedPaymentCurrencies = expandAllowedCurrencyIdsBySymbol(
      currencies,
      depositConfig.allowedPaymentCurrencies,
    );
    if (
      depositConfig.allowedPaymentCurrencies?.length &&
      transferAllowedPaymentCurrencies?.length
    ) {
      logger.info(
        `[MultichainTransfer] Expanded allowedPaymentCurrencies from ${depositConfig.allowedPaymentCurrencies.join(",")} to ${transferAllowedPaymentCurrencies.join(",")}`,
      );
    }
    const processed = processCurrencies(currencies, {
      excludeCurrencyTiers: depositConfig.excludeCurrencyTiers,
      allowedPaymentCurrencies: transferAllowedPaymentCurrencies,
      excludedPaymentCurrencies: depositConfig.excludedPaymentCurrencies,
    });

    logger.info(
      `[MultichainTransfer] Loaded ${processed.length} transfer currencies from ${currencies.length} supported currencies`,
    );

    setTransferCurrencies(processed);

    const savedSelection =
      loadTokenSelection() ||
      (depositConfig.defaultPaymentCurrency
        ? tokenSelectionFromPaymentCurrency(
            processed,
            allowedMultichainChains,
            depositConfig.defaultPaymentCurrency,
          )
        : undefined);
    if (
      savedSelection &&
      (await restoreSavedMultichainTransferSelection(processed, savedSelection))
    ) {
      return { processed, navigated: true };
    }

    if (await autoSelectFallbackMultichainTransferCurrency(processed)) {
      return { processed, navigated: true };
    }

    return { processed, navigated: false };
  }, [
    allowedMultichainChains,
    autoSelectFallbackMultichainTransferCurrency,
    depositConfig.allowedPaymentCurrencies,
    depositConfig.defaultPaymentCurrency,
    depositConfig.excludeCurrencyTiers,
    depositConfig.excludedPaymentCurrencies,
    glideConfig,
    restoreSavedMultichainTransferSelection,
  ]);

  useEffect(() => {
    if (open && depositConfig.embed && !hasPostedOpenMessage.current) {
      window.parent?.postMessage({ type: "open" }, "*");
      hasPostedOpenMessage.current = true;
    }
  }, [open, depositConfig.embed]);

  // Auto-start transfer-only flows without showing the funding source button.
  useEffect(() => {
    const initializeTransferOnlyFlow = async () => {
      if (
        step !== "initializing_transfer" ||
        hasInitializedTransferOnlyRef.current ||
        !isWalletReady
      ) {
        return;
      }

      hasInitializedTransferOnlyRef.current = true;
      setFundingSourceError(null);
      setWalletHasNoPaymentOptions(false);
      setIsLoadingTransferCurrencies(true);

      try {
        const { navigated } = await loadTransferCurrenciesAndMaybeSelect();
        setIsLoadingTransferCurrencies(false);

        if (navigated) {
          navigateToStep("multichain_transfer", true);
          return;
        }

        setFundingSourceError("No supported transfer currency available.");
        navigateToStep("funding_source", true);
      } catch (error) {
        logger.error(`Error fetching currencies: ${error}`);
        setFundingSourceError("Failed to load transfer currencies.");
        setIsLoadingTransferCurrencies(false);
        navigateToStep("funding_source", true);
      }
    };

    initializeTransferOnlyFlow();
  }, [
    loadTransferCurrenciesAndMaybeSelect,
    navigateToStep,
    step,
    isWalletReady,
  ]);

  // Listen for Option/Alt key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) {
        setIsOptionKeyPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.altKey) {
        setIsOptionKeyPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const showFooterSpacer =
    depositConfig.whitelabel &&
    !isConnected &&
    !canDisconnect &&
    !depositConfig.chain?.isTestnet;

  return (
    <div
      className={
        depositConfig.embed ? "" : "min-h-dvh bg-[--color-modal-backdrop]"
      }
    >
      <Dialog
        open={open}
        onClose={() => {
          // Allow closing on initial screens for each flow
          const initialFlowScreens = [
            "mode_selector", // Mode selection screen
            "funding_source", // Deposit initial screen (when multiple payment methods)
            "withdraw_payment_method", // Withdraw initial screen
            "initializing_transfer", // Transfer-only initial loading screen
          ];

          if (initialFlowScreens.includes(step)) {
            closeModal();
          }
          // All other screens: prevent closing on outside click
        }}
        size="sm"
        safeInsetBottom={depositConfig.safeInsetBottom}
      >
        <AnimateChangeInHeight>
          <div>
            {step === "creating_session" ||
            step === "wait_for_session_challenge" ||
            step === "estimate_transaction_amount" ||
            step === "initializing_transfer" ? (
              <>
                <TransactionCheckoutHeader />
                <div className="my-8 flex items-center justify-center">
                  <LoaderCircle className="h-10 w-10 animate-spin text-[--color-text-secondary]" />
                </div>
              </>
            ) : null}

            {step === "mode_selector" ? (
              <ModeSelector
                onSelect={async (mode) => {
                  const { isConnected } = await getConnectedWallet({
                    mode,
                  });

                  depositConfig.setMode(mode);

                  // Track mode selection
                  widgetAnalytics.modeSelected(mode);

                  if (depositConfig.embed) {
                    window.parent?.postMessage({ type: "setMode", mode }, "*");
                  }

                  if (mode === "deposit") {
                    navigateToStep("funding_source");
                  } else if (mode === "withdraw") {
                    if (!isConnected) {
                      throw new Error("Wallet not connected");
                    }
                    navigateToStep("withdraw_payment_method");
                  }
                }}
              />
            ) : null}

            {step === "buy_chain" ? (
              <TransferChooseChain onSelectChain={onBuyChooseChain} />
            ) : null}

            {step === "buy_token_selector" && currencyOptions?.length ? (
              <ChooseCurrency
                onBack={navigateBack}
                currencies={currencyOptions}
                onSelectCurrency={(currency) => {
                  depositConfig.setCurrency(currency);

                  // Track currency selection
                  widgetAnalytics.currencySelected(
                    currency.id,
                    currency.symbol,
                    depositConfig.chain?.id || "",
                  );

                  navigateToStep("deposit_amount");
                }}
              />
            ) : null}

            {step === "withdraw_payment_method" ? (
              <WithdrawPaymentMethod
                selectedPaymentMethod={paymentMethod as "wallet" | "interac"}
                onBack={stepStack.length > 1 ? navigateBack : undefined}
                onSelect={async (method) => {
                  setPaymentMethod(method);

                  // Track payment method selection
                  widgetAnalytics.paymentMethodSelected(method);

                  if (method === "wallet") {
                    navigateToStep("withdraw_dest_chain");
                  } else if (method === "interac") {
                    // For Interac withdrawals, select amount first
                    navigateToStep("deposit_amount");
                  }
                }}
              />
            ) : null}

            {step === "withdraw_interac_login" ? (
              <WithdrawWithInterac
                withdrawAmount={depositAmount}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  navigateToStep("success");
                }}
                onDeferredTransaction={closeModal}
              />
            ) : null}

            {step === "withdraw_dest" ? (
              <WithdrawDestination
                onSelect={() => {
                  setPaymentMethod("wallet");
                  navigateToStep("withdraw_dest_chain");
                }}
              />
            ) : null}

            {step === "deposit_amount" && paymentMethod ? (
              <DepositAmount
                isCreatingSession={isUpdatingSession}
                isOverCapacity={isOverCapacity}
                isInsufficientPaymentAmount={isInsufficientPaymentAmount}
                onUpdateDepositAmount={resetDepositAmountErrors}
                withdrawChain={withdrawDestinationChain}
                selectedWithdrawCurrency={selectedWithdrawCurrency}
                paymentMethod={paymentMethod}
                paymentOption={selectedPaymentOption}
                depositAmount={depositAmount}
                onChangeDepositAmount={setDepositAmount}
                depositAmountDenomination={depositAmountDenomination}
                onChangeDepositAmountDenomination={setDepositAmountDenomination}
                onChangePaymentOptions={() => navigateToStep("payment_options")}
                onChangeWithdrawCurrency={() =>
                  navigateToStep("withdraw_dest_currency")
                }
                onContinue={({ depositAmount }) => {
                  switch (paymentMethod) {
                    case "wallet":
                      createGlideSession(depositAmount);
                      break;
                    case "coinbase":
                      payWithCoinbaseOnramp(depositAmount);
                      break;
                    case "fiat":
                      payWithMoonPay(depositAmount);
                      break;
                    case "onramp":
                      payWithOnramp(depositAmount);
                      break;
                    case "onramper":
                      payWithOnramper();
                      break;
                    case "interac":
                      if (depositConfig.mode == "withdraw") {
                        setDepositAmount(depositAmount);
                        navigateToStep("withdraw_interac_login");
                        return;
                      } else {
                        payWithInterac(depositAmount);
                      }
                      break;
                    case "debit_card_alias":
                      if (depositConfig.debitCardAlias === "coinbase") {
                        payWithCoinbaseOnramp(depositAmount);
                      } else if (depositConfig.debitCardAlias === "fiat") {
                        payWithMoonPay(depositAmount);
                      }
                      break;
                    default:
                      if (!selectedPaymentOption) {
                        throw new Error("Payment option is not selected");
                      }

                      onChangePaymentOption(
                        selectedPaymentOption,
                        undefined,
                        depositAmount,
                      );
                  }
                }}
                onBack={navigateBack}
              />
            ) : null}

            {step === "withdraw_dest_currency" ? (
              <ChooseCurrency
                onBack={navigateBack}
                currencies={withdrawCurrencies || []}
                onSelectCurrency={(currency) => {
                  setSelectedWithdrawCurrency(currency);
                  navigateToStep("deposit_amount");
                }}
              />
            ) : null}

            {step === "connect_wallet" ? (
              <ConnectWallet
                onConnect={async () => {
                  // For pay mode with no amount, load payment options first then go to deposit_amount
                  if (
                    (depositConfig.mode === "pay" ||
                      depositConfig.mode === "call") &&
                    !depositConfig.hasFixedAmount
                  ) {
                    await loadWalletPaymentOptions();
                  } else {
                    return depositConfig.mode === "pay" ||
                      depositConfig.mode === "call"
                      ? createGlideSession()
                      : payWithWallet();
                  }
                }}
                onBack={navigateBack}
              />
            ) : null}

            {step === "recipient" && withdrawDestinationChain ? (
              <RecipientWalletAddress
                withdrawDestinationChain={withdrawDestinationChain}
                onBack={navigateBack}
                onContinue={(recipient) => {
                  depositConfig.setRecipient(recipient);
                  payWithWallet({
                    recipient,
                  });
                }}
              />
            ) : null}

            {step === "funding_source" ? (
              <FundingSource
                noPaymentOptions={walletHasNoPaymentOptions}
                errorMessage={fundingSourceError}
                availablePaymentMethods={availablePaymentMethods}
                isLoadingCapabilities={isLoadingCapabilities}
                txAmountUSD={txAmountUSD}
                showAppLogo={stepStack[0] === "funding_source"}
                paymentOptions={paymentOptions}
                isLoadingPaymentOptions={isLoadingPaymentOptions}
                isCreatingWalletSession={isCreatingWalletSession}
                selectedPaymentMethod={
                  isCreatingWalletSession
                    ? "wallet"
                    : isCreatingCoinbaseAppSession
                      ? "coinbase_app"
                      : isCreatingCoinbaseOnrampSession
                        ? depositConfig.debitCardAlias
                          ? "debit_card_alias"
                          : "coinbase"
                        : isCreatingMoonpayOnrampSession
                          ? depositConfig.debitCardAlias
                            ? "debit_card_alias"
                            : "fiat"
                          : isCreatingOnrampSession
                            ? "onramp"
                            : isCreatingInteracSession
                              ? "interac"
                              : isCreatingOnramperSession
                                ? "onramper"
                                : isLoadingTransferCurrencies
                                  ? "transfer"
                                  : undefined
                }
                onChangePaymentToken={
                  paymentOptions &&
                  paymentOptions.length > 0 &&
                  depositConfig.allowedPaymentCurrencies?.length !== 1
                    ? () => {
                        navigateToStep("payment_options");
                      }
                    : undefined
                }
                onSelect={async (paymentMethod) => {
                  setFundingSourceError(null);

                  // Track funding source selection
                  if (paymentMethod === "crypto") {
                    setWalletHasNoPaymentOptions(false);
                    widgetAnalytics.fundingSourceSelected("crypto");
                    navigateToStep("crypto_funding_source");
                    return;
                  }

                  if (paymentMethod === "onramp") {
                    setWalletHasNoPaymentOptions(false);
                    widgetAnalytics.fundingSourceSelected("fiat");
                    // Fetch capabilities before navigating
                    await fetchAvailablePaymentMethods();
                    navigateToStep("onramp_provider_selector");
                    return;
                  }

                  // Track specific payment method selection
                  widgetAnalytics.paymentMethodSelected(paymentMethod);

                  setPaymentMethod(paymentMethod);
                  switch (paymentMethod) {
                    case "wallet": {
                      const connectedWallet = await getConnectedWallet();

                      const useExternalWallet =
                        (await glideLocalStorage.getItem(
                          "glide.useExternalWallet",
                        )) === "true";
                      const useParentWallet =
                        (await glideLocalStorage.getItem(
                          "glide.useParentWallet",
                        )) === "true";

                      if (
                        connectedWallet.isConnected &&
                        (useExternalWallet || useParentWallet)
                      ) {
                        // If payment options are already loaded, check for saved token selection
                        if (
                          paymentOptions &&
                          paymentOptions.length > 0 &&
                          depositConfig.mode === "deposit"
                        ) {
                          const savedSelection = loadTokenSelection();
                          if (savedSelection) {
                            // Try to find a payment option matching the saved currency
                            const matchingOption = paymentOptions.find(
                              (po) =>
                                po.paymentCurrency ===
                                  savedSelection.currencyId ||
                                po.currencySymbol ===
                                  savedSelection.currencySymbol,
                            );

                            if (matchingOption) {
                              // Found matching option - select it
                              setSelectedPaymentOption(matchingOption);
                              navigateToStep("deposit_amount");
                              break;
                            }
                          }

                          // No saved selection, no match, or no balance - show token picker
                          navigateToStep("payment_options");
                        } else if (
                          (depositConfig.mode === "pay" ||
                            depositConfig.mode === "call") &&
                          !depositConfig.hasFixedAmount
                        ) {
                          // For pay mode with no amount, load payment options first
                          await loadWalletPaymentOptions();
                        } else {
                          // For deposit mode or pay mode with amount
                          return depositConfig.mode === "pay" ||
                            depositConfig.mode === "call"
                            ? createGlideSession()
                            : payWithWallet();
                        }
                      } else {
                        navigateToStep("connect_wallet");
                      }
                      break;
                    }
                    case "transfer": {
                      setWalletHasNoPaymentOptions(false);
                      // Load currencies while staying on current screen with button loading state
                      setIsLoadingTransferCurrencies(true);
                      try {
                        const { navigated } =
                          await loadTransferCurrenciesAndMaybeSelect();
                        if (navigated) {
                          setIsLoadingTransferCurrencies(false);
                          navigateToStep("multichain_transfer");
                          return;
                        }

                        setIsLoadingTransferCurrencies(false);
                        setFundingSourceError(
                          "No supported transfer currency available.",
                        );
                      } catch (error) {
                        logger.error(`Error fetching currencies: ${error}`);
                        setFundingSourceError(
                          "Failed to load transfer currencies.",
                        );
                        setIsLoadingTransferCurrencies(false);
                        // Don't navigate if fetch fails - stay on current screen
                      }
                      break;
                    }
                    case "coinbase":
                      if (
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ) {
                        // For pay mode with no amount, go to deposit_amount step first
                        if (!depositConfig.hasFixedAmount) {
                          navigateToStep("deposit_amount");
                        } else {
                          payWithCoinbaseOnramp(depositAmount);
                        }
                      } else {
                        navigateToStep("deposit_amount");
                      }
                      break;
                    case "coinbase_app":
                      createCoinbaseOAuthToken();
                      break;
                    case "fiat":
                      if (
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ) {
                        // For pay mode with no amount, go to deposit_amount step first
                        if (!depositConfig.hasFixedAmount) {
                          navigateToStep("deposit_amount");
                        } else {
                          payWithMoonPay(depositAmount);
                        }
                      } else {
                        navigateToStep("deposit_amount");
                      }
                      break;
                    case "interac":
                      if (
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ) {
                        // For pay mode with no amount, go to deposit_amount step first
                        if (!depositConfig.hasFixedAmount) {
                          navigateToStep("deposit_amount");
                        } else {
                          payWithInterac(depositAmount);
                        }
                      } else {
                        navigateToStep("deposit_amount");
                      }
                      break;
                    case "debit_card_alias":
                      if (
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ) {
                        // For pay mode with no amount, go to deposit_amount step first
                        if (!depositConfig.hasFixedAmount) {
                          navigateToStep("deposit_amount");
                        } else {
                          if (depositConfig.debitCardAlias === "coinbase") {
                            payWithCoinbaseOnramp(depositAmount);
                          } else if (depositConfig.debitCardAlias === "fiat") {
                            payWithMoonPay(depositAmount);
                          }
                        }
                      } else {
                        navigateToStep("deposit_amount");
                      }
                      break;
                    case "onramper":
                      if (
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ) {
                        if (!depositConfig.hasFixedAmount) {
                          navigateToStep("deposit_amount");
                        } else {
                          payWithOnramper();
                        }
                      } else {
                        payWithOnramper();
                      }
                      break;
                  }
                }}
                onReset={navigateStartOver}
              />
            ) : null}

            {step === "onramp_provider_selector" ? (
              <OnrampProviderSelector
                availablePaymentMethods={availablePaymentMethods}
                txAmountUSD={txAmountUSD}
                selectedPaymentMethod={
                  isCreatingCoinbaseOnrampSession
                    ? depositConfig.debitCardAlias
                      ? "debit_card_alias"
                      : "coinbase"
                    : isCreatingMoonpayOnrampSession
                      ? depositConfig.debitCardAlias
                        ? "debit_card_alias"
                        : "fiat"
                      : isCreatingInteracSession
                        ? "interac"
                        : isCreatingOnramperSession
                          ? "onramper"
                          : undefined
                }
                onSelect={async (paymentMethod) => {
                  widgetAnalytics.paymentMethodSelected(paymentMethod);
                  setPaymentMethod(paymentMethod);

                  // For pay/call mode, create session directly with txAmountUSD
                  if (
                    (depositConfig.mode === "pay" ||
                      depositConfig.mode === "call") &&
                    txAmountUSD
                  ) {
                    const amountStr = txAmountUSD.toString();
                    switch (paymentMethod) {
                      case "coinbase":
                      case "debit_card_alias":
                        if (
                          paymentMethod === "debit_card_alias" &&
                          depositConfig.debitCardAlias === "fiat"
                        ) {
                          await payWithMoonPay(amountStr);
                        } else {
                          await payWithCoinbaseOnramp(amountStr);
                        }
                        break;
                      case "fiat":
                        await payWithMoonPay(amountStr);
                        break;
                      case "interac":
                        await payWithInterac(amountStr);
                        break;
                      case "onramper":
                        await payWithOnramper();
                        break;
                    }
                  } else {
                    if (paymentMethod === "onramper") {
                      await payWithOnramper();
                      return;
                    }
                    // For deposit mode, go to amount selection
                    navigateToStep("deposit_amount");
                  }
                }}
                onBack={navigateBack}
              />
            ) : null}

            {step === "crypto_funding_source" ? (
              <CryptoFundingSource
                selectedPaymentMethod={
                  isCreatingWalletSession
                    ? "wallet"
                    : isCreatingCoinbaseAppSession
                      ? "coinbase_app"
                      : undefined
                }
                onBack={navigateBack}
                onSelect={async (paymentMethod) => {
                  // Track payment method selection
                  widgetAnalytics.paymentMethodSelected(paymentMethod);

                  setPaymentMethod(paymentMethod);
                  switch (paymentMethod) {
                    case "wallet": {
                      const connectedWallet = await getConnectedWallet();

                      if (connectedWallet.isConnected) {
                        // Wallet is connected (either external or parent app)
                        // If payment options are already loaded, check for saved token selection
                        if (paymentOptions && paymentOptions.length > 0) {
                          const savedSelection = loadTokenSelection();
                          if (savedSelection) {
                            // Try to find a payment option matching the saved currency
                            const matchingOption = paymentOptions.find(
                              (po) =>
                                po.paymentCurrency ===
                                  savedSelection.currencyId ||
                                po.currencySymbol ===
                                  savedSelection.currencySymbol,
                            );

                            if (matchingOption) {
                              // Found matching option - select it
                              setSelectedPaymentOption(matchingOption);

                              // For pay/call mode with amount, create session directly
                              if (
                                (depositConfig.mode === "pay" ||
                                  depositConfig.mode === "call") &&
                                depositConfig.amount &&
                                Number(depositConfig.amount) > 0
                              ) {
                                createGlideSession();
                              } else {
                                // For deposit mode or pay/call without amount, go to amount screen
                                navigateToStep("deposit_amount");
                              }
                              break;
                            }
                          }

                          // No saved selection, no match, or no balance - show token picker
                          navigateToStep("payment_options");
                        } else if (
                          (depositConfig.mode === "pay" ||
                            depositConfig.mode === "call") &&
                          !depositConfig.hasFixedAmount
                        ) {
                          // For pay mode with no amount, load payment options first
                          await loadWalletPaymentOptions();
                        } else {
                          // For deposit mode or pay mode with amount
                          return depositConfig.mode === "pay" ||
                            depositConfig.mode === "call"
                            ? createGlideSession()
                            : payWithWallet();
                        }
                      } else {
                        navigateToStep("connect_wallet");
                      }
                      break;
                    }
                    case "transfer": {
                      setWalletHasNoPaymentOptions(false);
                      // Load currencies while staying on current screen with button loading state
                      setIsLoadingTransferCurrencies(true);
                      try {
                        const { navigated } =
                          await loadTransferCurrenciesAndMaybeSelect();
                        if (navigated) {
                          setIsLoadingTransferCurrencies(false);
                          navigateToStep("multichain_transfer");
                          return;
                        }

                        setIsLoadingTransferCurrencies(false);
                        setFundingSourceError(
                          "No supported transfer currency available.",
                        );
                      } catch (error) {
                        logger.error(`Error fetching currencies: ${error}`);
                        setFundingSourceError(
                          "Failed to load transfer currencies.",
                        );
                        setIsLoadingTransferCurrencies(false);
                        // Don't navigate if fetch fails - stay on current screen
                      }
                      break;
                    }
                    case "coinbase_app":
                      createCoinbaseOAuthToken();
                      break;
                  }
                }}
              />
            ) : null}

            {step === "payment_options" &&
            paymentOptions &&
            (paymentMethod === "transfer" ||
              paymentMethod === "wallet" ||
              paymentMethod === "coinbase_app") ? (
              <TransactionCheckoutPaymentOptionsList
                paymentMethod={paymentMethod}
                paymentOptions={paymentOptions}
                onSelect={(po) => {
                  if (
                    depositConfig.mode === "deposit" ||
                    depositConfig.mode === "withdraw"
                  ) {
                    setSelectedPaymentOption(po);
                    navigateToStep("deposit_amount", true);
                  } else if (
                    (depositConfig.mode === "pay" ||
                      depositConfig.mode === "call") &&
                    !depositConfig.hasFixedAmount
                  ) {
                    // For pay/call mode with no amount, go back to amount screen
                    setSelectedPaymentOption(po);
                    navigateToStep("deposit_amount", true);
                  } else {
                    onChangePaymentOption(po);
                  }
                }}
                onBack={navigateBack}
                onReset={navigateStartOver}
              />
            ) : null}

            {step === "pay_with_coinbase_onramp" && glideSession ? (
              <PayWithCoinbaseOnramp
                session={glideSession}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onOverpaymentRefund={handleOverpaymentRefund}
              />
            ) : null}

            {step === "pay_with_moonpay" && glideSession ? (
              <PayWithMoonPay
                session={glideSession}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onOverpaymentRefund={handleOverpaymentRefund}
              />
            ) : null}

            {step === "pay_with_onramp" && glideSession ? (
              <PayWithOnramp
                session={glideSession}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
              />
            ) : null}

            {step === "pay_with_interac" && glideSession ? (
              <PayWithInterac
                session={glideSession}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onOverpaymentRefund={handleOverpaymentRefund}
              />
            ) : null}

            {step === "pay_with_onramper" && glideSession ? (
              <PayWithOnramper
                session={glideSession}
                popup={onramperPopupRef.current}
                onBack={navigateBack}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
              />
            ) : null}

            {step === "transfer_choose_chain" &&
            ["deposit", "pay", "call", "buy"].includes(
              depositConfig.mode || "",
            ) ? (
              <TransferChooseChain
                onBack={stepStack?.length > 1 ? navigateBack : undefined}
                onSelectChain={(chain) => {
                  onTransferChooseChain(chain);
                }}
              />
            ) : null}

            {step === "transfer" &&
            ((isBitcoinMainnetSession &&
              glideSession &&
              (bitcoinLightningSession ||
                depositConfig.excludeChainIds?.includes(lightning.id))) ||
              (!isBitcoinMainnetSession && glideSession)) ? (
              <Transfer
                onBack={navigateBack}
                session={glideSession}
                bitcoinLightningSession={bitcoinLightningSession}
                onSuccess={onTransferSuccess}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onRequestRefund={handleRequestRefund}
                onOverpaymentRefund={handleOverpaymentRefund}
                onSessionUpdate={setGlideSession}
                selectedCurrency={selectedMultichainCurrency}
                onRegenerateAddress={
                  selectedPaymentOption
                    ? async () => {
                        await onChangePaymentOption(
                          selectedPaymentOption,
                          "transfer",
                          undefined,
                          true,
                        );
                      }
                    : undefined
                }
                onShowSupportedChains={(chains: Chain[]) => {
                  // Store the chains to display
                  setChainsToDisplay(chains);
                  navigateToStep("multichain_view_supported_chains");
                }}
              />
            ) : null}

            {step === "multichain_transfer" && selectedMultichainType ? (
              <MultichainTransfer
                onBack={stepStack.length > 1 ? navigateBack : undefined}
                onSuccess={onTransferSuccess}
                excludeBitcoin={isBitcoinBelowMinimum}
                selectedChainType={selectedMultichainType}
                selectedCurrency={selectedMultichainCurrency}
                selectedCurrencies={selectedMultichainCurrencies}
                allAvailableCurrencies={transferCurrencies}
                evmSession={multichainEvmSession}
                solanaSession={multichainSolanaSession}
                bitcoinSession={multichainBitcoinSession}
                bitcoinLightningSession={multichainBitcoinLightningSession}
                tronSession={multichainTronSession}
                onSessionCreated={handleMultichainSessionCreated}
                onLightningSessionCreated={
                  handleMultichainLightningSessionCreated
                }
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onRequestRefund={handleRequestRefund}
                onOverpaymentRefund={handleOverpaymentRefund}
                onCreateSession={async (chainType, specificChain, currency) => {
                  // Update parent state so component re-renders with new selection
                  if (currency) {
                    setSelectedMultichainCurrency(currency);
                    // Find all currencies with the same symbol
                    const symbol = currency.symbol;
                    const allWithSymbol =
                      transferCurrencies?.filter((c) => c.symbol === symbol) ||
                      [];
                    setSelectedMultichainCurrencies(allWithSymbol);
                  }
                  setSelectedMultichainType(chainType);

                  // Create the new session
                  await createMultichainSession(
                    chainType,
                    specificChain,
                    currency,
                  );
                }}
                onRegenerateAddress={async (
                  chainType,
                  specificChain,
                  currency,
                ) => {
                  await createMultichainSession(
                    chainType,
                    specificChain,
                    currency,
                    true,
                  );
                }}
              />
            ) : null}

            {step === "multichain_supported_tokens_chain" ? (
              <TransferChooseChain
                onBack={navigateBack}
                onSelectChain={async (chain) => {
                  const currencies = await listSupportedCurrencies(
                    glideConfig,
                    {
                      chainId: chain.id,
                      all: false,
                    },
                  );
                  setCurrencyOptions(currencies);
                  navigateToStep("multichain_supported_tokens_currency");
                }}
              />
            ) : null}

            {step === "multichain_supported_tokens_currency" &&
            currencyOptions?.length ? (
              <ChooseCurrency
                onBack={navigateBack}
                currencies={currencyOptions}
                onSelectCurrency={() => {
                  // Navigate back to multichain transfer after viewing the list
                  // Reset the navigation stack after viewing the list.
                  setStepStack(["multichain_transfer"]);
                  setStep("multichain_transfer");
                }}
              />
            ) : null}

            {step === "multichain_supported_chains_for_currency" &&
            currencyOptions?.length ? (
              <TransferChooseChain
                onBack={navigateBack}
                onSelectChain={async (chain) => {
                  // Show supported chains for the selected currency
                  const currency = currencyOptions[0];
                  const currencies = await listSupportedCurrencies(
                    glideConfig,
                    {
                      chainId: chain.id,
                      all: false,
                    },
                  );
                  // Filter to show only the selected currency if it exists on this chain
                  const filteredCurrencies = currencies.filter(
                    (c) => c.symbol === currency.symbol,
                  );
                  setCurrencyOptions(filteredCurrencies);
                  // Navigate back to multichain transfer after showing the chain info.
                  setStepStack(["multichain_transfer"]);
                  setStep("multichain_transfer");
                }}
              />
            ) : null}

            {step === "multichain_view_supported_chains" &&
            chainsToDisplay.length ? (
              <SupportedChainsList
                chains={chainsToDisplay}
                onBack={navigateBack}
              />
            ) : null}

            {step === "pay_with_coinbase_app" &&
            glideSession &&
            coinbaseAccessToken ? (
              <PayWithCoinbaseApp
                onBack={navigateBack}
                session={glideSession}
                accessToken={coinbaseAccessToken}
                onSuccess={(session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onOverpaymentRefund={handleOverpaymentRefund}
              />
            ) : null}

            {step === "withdraw_dest_chain" ? (
              <TransferChooseChain
                onBack={navigateBack}
                onSelectChain={(chain: Chain) => {
                  setWithdrawDestinationChain(chain);
                  navigateToStep("recipient");
                }}
              />
            ) : null}

            {step === "review" && glideSession ? (
              <ReviewSession
                session={glideSession}
                routes={routes || []}
                onBack={navigateBack}
                onSuccess={async (session) => {
                  setGlideSession(session);
                  if (depositConfig.embed && depositConfig.autoCloseOnSuccess) {
                    closeModal();
                  } else {
                    navigateToStep("success");
                  }
                  postSuccessMessage(session);
                }}
                coinbaseAccessToken={coinbaseAccessToken}
                onDeferredTransaction={closeModal}
                onMFA={() => navigateToStep("pay_with_coinbase_app")}
                onNavigateToGasRefillExplainer={() =>
                  navigateToStep("gas_refill_explainer")
                }
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
                onOverpaymentRefund={handleOverpaymentRefund}
                onRoutePriorityChange={async (priority) => {
                  const updatedSession = await createGlideSession(
                    undefined,
                    undefined,
                    undefined,
                    priority,
                  );
                  if (!updatedSession) {
                    throw new Error(
                      "[onRoutePriorityChange] Failed to create Glide session",
                    );
                  }
                  return updatedSession!;
                }}
                onChangePaymentToken={
                  paymentOptions &&
                  paymentOptions.length > 0 &&
                  depositConfig.allowedPaymentCurrencies?.length !== 1
                    ? () => {
                        navigateToStep("payment_options");
                      }
                    : undefined
                }
              />
            ) : null}

            {step === "success" && glideSession ? (
              <TransactionCheckoutSuccess
                session={glideSession}
                onClose={closeModal}
                onInsufficientPaymentAmount={handleInsufficientPaymentAmount}
              />
            ) : null}

            {step === "gas_refill_explainer" &&
            glideSession &&
            Number(glideSession.gasRefuelAmount) > 0 ? (
              <GasRefillExplainer
                onBack={navigateBack}
                gasRefuelAmount={glideSession.gasRefuelAmount!}
                gasRefuelUSD={glideSession.gasRefuelUSD!}
                chainLogoUrl={glideSession.sponsoredTransactionChainLogoUrl}
                chainName={glideSession.sponsoredTransactionChainName}
                gasCurrencySymbol={glideSession.gasCurrencySymbol!}
                gasCurrencyLogoUrl={glideSession.gasCurrencyLogoUrl!}
              />
            ) : null}

            {step === "debug" ? <DebugStep onBack={navigateBack} /> : null}

            {step === "error" ? (
              <div>
                <div className="my-4 flex flex-col items-center justify-center">
                  <CircleX className="h-12 w-12 text-red-500" />
                  <div className="mt-2 font-medium text-lg text-center text-[--color-text-primary]">
                    Refund Not Available
                  </div>
                  <div className="text-[--color-text-secondary] text-center text-sm">
                    This payment is not eligible for a refund. For support,
                    email support@buildwithglide.com.
                  </div>
                </div>
              </div>
            ) : null}

            {step === "confirm_refund" && failedSession ? (
              <ConfirmRefund
                session={failedSession}
                refundToken={refundToken}
                onBack={navigateBack}
                onConfirm={handleInsufficientPaymentAmount}
              />
            ) : null}

            {step === "insufficient_payment_amount" && failedSession ? (
              <InsufficientPaymentAmount
                session={failedSession}
                refundToken={refundToken}
              />
            ) : null}

            {step === "excess_payment_refund" && failedSession ? (
              <OverpaymentRefund
                session={failedSession}
                refundToken={refundToken}
              />
            ) : null}
          </div>

          {showFooterSpacer ? (
            <div className="min-h-2" />
          ) : (
            <div className="mt-6 -mx-2">
              <div className="flex justify-between items-center leading-[0] gap-2 px-3">
                <div className="flex-1 flex  pl-1">
                  {depositConfig.whitelabel ? null : (
                    <a
                      className="transition-all opacity-50 hover:opacity-100 grayscale hover:grayscale-0"
                      href={
                        depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                          ? "https://docs.paywithglide.xyz/resources/pay-with-glide"
                          : "https://docs.paywithglide.xyz/resources/deposit-with-glide"
                      }
                      target="_blank"
                    >
                      <GlideLogo className="-mr-1 -mt-0.5 h-3 w-auto inline" />
                    </a>
                  )}
                </div>

                <div className="flex-1 flex gap-3 justify-end pr-1 -mb-0.5">
                  {depositConfig.debug ||
                  depositConfig.chain?.isTestnet ||
                  isOptionKeyPressed ? (
                    <button
                      className="text-xs text-[--color-text-secondary] opacity-70 hover:opacity-100 border-b border-transparent hover:border-[--color-text-secondary] transition-all"
                      onClick={() => navigateToStep("debug")}
                    >
                      Debug
                    </button>
                  ) : null}

                  {depositConfig.chain?.isTestnet ? (
                    <div className="text-xs text-[--color-alert-warning-accent]">
                      Testnet
                    </div>
                  ) : null}

                  {depositConfig.whitelabel ? null : (
                    <>
                      <a
                        className="text-xs text-[--color-text-secondary] opacity-70 hover:opacity-100 border-b border-transparent hover:border-[--color-text-secondary] transition-all"
                        href="https://paywithglide.xyz/terms"
                        target="_blank"
                      >
                        Terms
                      </a>
                      <a
                        className="text-xs text-[--color-text-secondary] opacity-70 hover:opacity-100 border-b border-transparent hover:border-[--color-text-secondary] transition-all"
                        href="https://docs.paywithglide.xyz/resources/support"
                        target="_blank"
                      >
                        Support
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {depositConfig.embed ? (
            <button
              className="p-1 transition-colors hover:bg-[--color-background-secondary] absolute top-4 right-4 rounded-lg text-[--color-text-primary]"
              onClick={() => closeModal()}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </AnimateChangeInHeight>
      </Dialog>
    </div>
  );
}
